/**
 * Copy and configuration for the specialty landing pages
 * (/tajweed-classes, /hifz-program, /ijazah, /quranic-arabic,
 * /quran-for-adults, /female-quran-teachers, /online-quran-classes,
 * /quran-classes-for-beginners, /noorani-qaida, /one-to-one-quran-classes,
 * /quran-classes-usa, /quran-classes-uk).
 *
 * These pages are content, not UI chrome, so both languages live here side
 * by side instead of as ~400 flat translation keys each. Every claim below
 * must stay true of the live platform: teacher counts and ratings come from
 * the listing at render time, never from this file.
 */

export interface LandingCopy {
  seo: { title: string; description: string; keywords: string };
  breadcrumb: string;
  hero: {
    badge: string;
    title: string;
    subtitle: string;
    ctaPrimary: string;
    ctaSecondary: string;
    trust: string[];
  };
  stats: {
    teachersLabel: string;
    ratingLabel: string;
    a: { value: string; label: string };
    b: { value: string; label: string };
  };
  why: { eyebrow: string; title: string; items: { title: string; desc: string }[] };
  tracks: {
    eyebrow: string;
    title: string;
    subtitle: string;
    items: { tag: string; title: string; bullets: string[] }[];
  };
  teachers: { eyebrow: string; title: string; subtitle: string; viewAll: string; empty: string; error: string };
  courses: { eyebrow: string; title: string; viewAll: string };
  how: { eyebrow: string; title: string; items: { title: string; desc: string }[] };
  faq: { eyebrow: string; title: string; items: { q: string; a: string }[] };
  cta: { title: string; subtitle: string; primary: string; secondary: string };
}

export interface LandingConfig {
  path: string;
  teacherFilter: { specialty?: string; gender?: 'Male' | 'Female'; ijazah_only?: boolean; language?: string };
  courseFilter: { category?: string; level?: string };
  primaryHref: string;
  en: LandingCopy;
  ar: LandingCopy;
}

const COMMON_EN = {
  teachersLabel: 'Teachers on this page',
  ratingLabel: 'Average rating',
  viewAll: 'See all teachers',
  empty: 'No teachers match this page right now. Browse the full list.',
  error: 'Could not load teachers. Please try again.',
  coursesViewAll: 'Browse all courses',
  howEyebrow: 'How it works',
  howTitle: 'From first click to first lesson',
  how: [
    { title: 'Create a free account', desc: 'Sign up in under a minute. No card is needed to look around.' },
    { title: 'Choose your teacher', desc: 'Filter by specialty, language, price and gender, read reviews, and pick a time that suits you.' },
    { title: 'Book a trial', desc: 'Many teachers offer a trial session so you can check the fit before committing.' },
    { title: 'Learn, live, one to one', desc: 'Sessions run on video, with homework, recordings and progress tracking in your dashboard.' },
  ],
  faqEyebrow: 'FAQ',
  faqTitle: 'Questions people ask before booking',
  ctaSecondary: 'Browse teachers',
};

const COMMON_AR = {
  teachersLabel: 'معلمو هذه الصفحة',
  ratingLabel: 'متوسط التقييم',
  viewAll: 'عرض كل المعلمين',
  empty: 'لا يوجد معلمون مطابقون لهذه الصفحة حاليًا. تصفح القائمة الكاملة.',
  error: 'تعذر تحميل المعلمين. حاول مرة أخرى.',
  coursesViewAll: 'تصفح كل الدورات',
  howEyebrow: 'كيف تبدأ',
  howTitle: 'من أول نقرة إلى أول درس',
  how: [
    { title: 'أنشئ حسابًا مجانيًا', desc: 'التسجيل يستغرق أقل من دقيقة، ولا حاجة لبطاقة دفع للتصفح.' },
    { title: 'اختر معلمك', desc: 'صفِّ حسب التخصص واللغة والسعر والجنس، واقرأ التقييمات، واختر الوقت المناسب لك.' },
    { title: 'احجز جلسة تجريبية', desc: 'كثير من المعلمين يقدمون جلسة تجريبية لتتأكد من الملاءمة قبل الالتزام.' },
    { title: 'تعلّم مباشرة، فردًا لفرد', desc: 'الجلسات عبر الفيديو، مع واجبات وتسجيلات ومتابعة للتقدم في لوحتك.' },
  ],
  faqEyebrow: 'الأسئلة الشائعة',
  faqTitle: 'ما يسأله الناس قبل الحجز',
  ctaSecondary: 'تصفح المعلمين',
};

export const LANDINGS: Record<string, LandingConfig> = {
  'tajweed-classes': {
    path: '/tajweed-classes',
    teacherFilter: { specialty: 'Tajweed' },
    courseFilter: { category: 'Tajweed' },
    primaryHref: '/register/student',
    en: {
      seo: {
        title: 'Online Tajweed Classes with Certified Teachers | Hudan',
        description: 'Learn Tajweed one to one with Ijazah-certified teachers. Makharij, rules of noon and meem, madd, and corrected recitation, live on video, in English or Arabic.',
        keywords: 'tajweed classes online, learn tajweed, tajweed rules, tajweed teacher, دروس تجويد, تعلم التجويد أونلاين',
      },
      breadcrumb: 'Tajweed classes',
      hero: {
        badge: 'Live, one to one',
        title: 'Learn Tajweed from teachers who hear every letter',
        subtitle: 'Correct recitation is learned by reciting to someone who stops you at the right moment. Book live sessions with Ijazah-certified Tajweed teachers, from your first letters to a polished, rule-by-rule recitation.',
        ctaPrimary: 'Start with a trial session',
        ctaSecondary: 'See Tajweed teachers',
        trust: ['Ijazah-certified teachers', 'Sessions in English or Arabic', 'Male and female teachers', 'Recordings and homework included'],
      },
      stats: {
        ...COMMON_EN,
        a: { value: 'Hafs', label: 'Riwayah taught by default' },
        b: { value: '1 to 1', label: 'Session format' },
      },
      why: {
        eyebrow: 'Why learn Tajweed here',
        title: 'Rules you can hear, not just read',
        items: [
          { title: 'Listen and correct', desc: 'You recite, the teacher corrects in real time. This is how Tajweed has been taught for centuries; video only removes the travel.' },
          { title: 'Makharij first', desc: 'Articulation points and letter attributes come before rule names, so the rules land on sounds you can already make.' },
          { title: 'A clear path', desc: 'From the Noorani Qaida through the rules of noon saakinah, meem, madd and waqf to full surah recitation, in an order that builds.' },
          { title: 'Your pace, your schedule', desc: 'Weekly or daily, mornings or late evenings, teachers across time zones keep a slot open when you are free.' },
          { title: 'Progress you can see', desc: 'Homework with teacher grading, session recordings to replay, and notes per session in your dashboard.' },
          { title: 'Towards Ijazah, if you wish', desc: 'Students who reach a consistent, error-free recitation can continue with the same teacher into an Ijazah track.' },
        ],
      },
      tracks: {
        eyebrow: 'Where to start',
        title: 'Three starting points',
        subtitle: 'Tell your teacher which one sounds like you and the first session is planned around it.',
        items: [
          { tag: 'Beginner', title: 'I can read Arabic slowly', bullets: ['Letters, harakat and joining with the Noorani Qaida', 'Makharij of the letters with drills', 'Short surahs of Juz Amma, corrected line by line'] },
          { tag: 'Intermediate', title: 'I read, but make mistakes', bullets: ['Rules of noon saakinah, tanween and meem saakinah', 'Types of madd and their lengths', 'Waqf and ibtida: where to stop and start'] },
          { tag: 'Advanced', title: 'I want a polished recitation', bullets: ['Fine points of letter attributes and tafkheem', 'Full-surah recitation with detailed feedback', 'Preparation for an Ijazah in Hafs an Asim'] },
        ],
      },
      teachers: { eyebrow: 'Teachers', title: 'Tajweed teachers on Hudan', subtitle: 'Every teacher on this page lists Tajweed as a specialty. Ratings come from students who took sessions.', viewAll: COMMON_EN.viewAll, empty: COMMON_EN.empty, error: COMMON_EN.error },
      courses: { eyebrow: 'Courses', title: 'Structured Tajweed courses', viewAll: COMMON_EN.coursesViewAll },
      how: { eyebrow: COMMON_EN.howEyebrow, title: COMMON_EN.howTitle, items: COMMON_EN.how },
      faq: {
        eyebrow: COMMON_EN.faqEyebrow,
        title: COMMON_EN.faqTitle,
        items: [
          { q: 'Do I need to read Arabic before starting Tajweed?', a: 'No. Teachers start beginners with the Noorani Qaida, which teaches the letters, vowels and joining together with correct pronunciation. Tajweed rules are introduced once you can read slowly.' },
          { q: 'Which riwayah do you teach?', a: 'Hafs an Asim by default, the riwayah used in most of the Muslim world. Some teachers also list other qiraat on their profile.' },
          { q: 'How long does it take to learn Tajweed?', a: 'Most adults reading slowly reach a correct, rule-aware recitation of short surahs within a few months of one or two sessions a week with daily practice. Mastery across the whole Quran takes longer and depends on your practice.' },
          { q: 'Can I learn Tajweed in English?', a: 'Yes. Many teachers explain the rules in English and use Arabic for the recitation itself. Filter the teacher list by language.' },
          { q: 'Is there a female Tajweed teacher?', a: 'Yes. Use the gender filter on the teachers page or visit the female teachers page.' },
          { q: 'What happens in a trial session?', a: 'The teacher listens to you recite, places you on the path above, and agrees a plan and schedule with you. There is no obligation to continue.' },
        ],
      },
      cta: { title: 'Your next recitation can be your best yet', subtitle: 'Book a trial session and hear the difference a teacher makes.', primary: 'Start with a trial session', secondary: COMMON_EN.ctaSecondary },
    },
    ar: {
      seo: {
        title: 'دروس تجويد أونلاين مع معلمين مُجازين | هُدًى للمتقين',
        description: 'تعلّم التجويد فردًا لفرد مع معلمين مُجازين: مخارج الحروف، أحكام النون والميم، المدود، وتصحيح التلاوة مباشرة عبر الفيديو بالعربية أو الإنجليزية.',
        keywords: 'دروس تجويد, تعلم التجويد أونلاين, أحكام التجويد, معلم تجويد, تعليم التجويد للمبتدئين, tajweed classes online',
      },
      breadcrumb: 'دروس التجويد',
      hero: {
        badge: 'مباشر، فردًا لفرد',
        title: 'تعلّم التجويد مع معلمين يسمعون كل حرف',
        subtitle: 'التلاوة الصحيحة تُتعلَّم بالقراءة على من يوقفك عند الخطأ في لحظته. احجز جلسات مباشرة مع معلمي تجويد مُجازين، من أولى حروفك إلى تلاوة متقنة حكمًا بعد حكم.',
        ctaPrimary: 'ابدأ بجلسة تجريبية',
        ctaSecondary: 'شاهد معلمي التجويد',
        trust: ['معلمون مُجازون بالسند', 'جلسات بالعربية أو الإنجليزية', 'معلمون ومعلمات', 'تسجيلات وواجبات ضمن الجلسة'],
      },
      stats: {
        ...COMMON_AR,
        a: { value: 'حفص', label: 'الرواية المعتمدة افتراضيًا' },
        b: { value: 'فردي', label: 'نمط الجلسة' },
      },
      why: {
        eyebrow: 'لماذا تتعلم التجويد هنا',
        title: 'أحكام تسمعها، لا تقرؤها فقط',
        items: [
          { title: 'اقرأ ويُصحَّح لك', desc: 'أنت تتلو والمعلم يصحح في اللحظة نفسها. هكذا عُلِّم التجويد عبر القرون، والفيديو ألغى المسافة فقط.' },
          { title: 'المخارج أولًا', desc: 'مخارج الحروف وصفاتها قبل أسماء الأحكام، فتقع الأحكام على أصوات تُحسنها بالفعل.' },
          { title: 'مسار واضح', desc: 'من القاعدة النورانية إلى أحكام النون الساكنة والميم والمدود والوقف، حتى تلاوة السور كاملة، بترتيب يبني بعضه بعضًا.' },
          { title: 'على وقتك وبوتيرتك', desc: 'أسبوعيًا أو يوميًا، صباحًا أو في وقت متأخر، معلمون في مناطق زمنية مختلفة يجدون لك موعدًا حين تكون متفرغًا.' },
          { title: 'تقدم تراه بعينك', desc: 'واجبات يقيّمها المعلم، وتسجيلات للجلسات تعيدها متى شئت، وملاحظات لكل جلسة في لوحتك.' },
          { title: 'نحو الإجازة إن أردت', desc: 'من يصل إلى تلاوة متقنة خالية من الخطأ يمكنه المتابعة مع المعلم نفسه في مسار الإجازة.' },
        ],
      },
      tracks: {
        eyebrow: 'من أين تبدأ',
        title: 'ثلاث نقاط بداية',
        subtitle: 'أخبر معلمك أيها يشبه حالك، وتُخطَّط الجلسة الأولى على أساسه.',
        items: [
          { tag: 'مبتدئ', title: 'أقرأ العربية ببطء', bullets: ['الحروف والحركات والوصل بالقاعدة النورانية', 'مخارج الحروف بتدريبات عملية', 'قصار سور جزء عمّ مصححة سطرًا سطرًا'] },
          { tag: 'متوسط', title: 'أقرأ لكني أخطئ', bullets: ['أحكام النون الساكنة والتنوين والميم الساكنة', 'أنواع المدود ومقاديرها', 'الوقف والابتداء: أين تقف وأين تبدأ'] },
          { tag: 'متقدم', title: 'أريد تلاوة متقنة', bullets: ['دقائق صفات الحروف والتفخيم والترقيق', 'تلاوة سور كاملة بملاحظات تفصيلية', 'التهيئة لإجازة برواية حفص عن عاصم'] },
        ],
      },
      teachers: { eyebrow: 'المعلمون', title: 'معلمو التجويد في هُدًى', subtitle: 'كل معلم في هذه الصفحة يذكر التجويد ضمن تخصصاته، والتقييمات من طلاب أخذوا جلسات فعلًا.', viewAll: COMMON_AR.viewAll, empty: COMMON_AR.empty, error: COMMON_AR.error },
      courses: { eyebrow: 'الدورات', title: 'دورات تجويد منظمة', viewAll: COMMON_AR.coursesViewAll },
      how: { eyebrow: COMMON_AR.howEyebrow, title: COMMON_AR.howTitle, items: COMMON_AR.how },
      faq: {
        eyebrow: COMMON_AR.faqEyebrow,
        title: COMMON_AR.faqTitle,
        items: [
          { q: 'هل يلزمني إتقان القراءة قبل تعلم التجويد؟', a: 'لا. يبدأ المعلمون مع المبتدئين بالقاعدة النورانية التي تعلّم الحروف والحركات والوصل مع النطق الصحيح، ثم تُقدَّم الأحكام حين تقرأ ولو ببطء.' },
          { q: 'بأي رواية تدرّسون؟', a: 'رواية حفص عن عاصم افتراضيًا، وهي الرواية الشائعة في معظم العالم الإسلامي. وبعض المعلمين يذكرون قراءات أخرى في ملفاتهم.' },
          { q: 'كم يستغرق تعلم التجويد؟', a: 'أغلب الكبار الذين يقرؤون ببطء يصلون إلى تلاوة صحيحة لقصار السور مع وعي بالأحكام خلال أشهر قليلة بجلسة أو جلستين أسبوعيًا مع تدريب يومي. أما الإتقان في القرآن كله فيحتاج وقتًا أطول بحسب مثابرتك.' },
          { q: 'هل يمكنني تعلم التجويد بالإنجليزية؟', a: 'نعم. كثير من المعلمين يشرحون الأحكام بالإنجليزية ويستخدمون العربية للتلاوة نفسها. صفِّ قائمة المعلمين حسب اللغة.' },
          { q: 'هل توجد معلمة تجويد؟', a: 'نعم. استخدم مرشح الجنس في صفحة المعلمين أو زر صفحة المعلمات.' },
          { q: 'ماذا يحدث في الجلسة التجريبية؟', a: 'يستمع المعلم لتلاوتك، ويحدد موضعك في المسار أعلاه، ويتفق معك على خطة ومواعيد. ولا التزام عليك بالمتابعة.' },
        ],
      },
      cta: { title: 'تلاوتك القادمة قد تكون أفضل تلاوة لك', subtitle: 'احجز جلسة تجريبية واسمع الفرق الذي يصنعه المعلم.', primary: 'ابدأ بجلسة تجريبية', secondary: COMMON_AR.ctaSecondary },
    },
  },

  'hifz-program': {
    path: '/hifz-program',
    teacherFilter: { specialty: 'Hifz' },
    courseFilter: { category: 'Memorization' },
    primaryHref: '/register/student',
    en: {
      seo: {
        title: 'Online Quran Memorization (Hifz) Program | Hudan',
        description: 'Memorize the Quran with a personal Hifz teacher: daily portions, structured revision, a digital Mushaf that tracks every ayah, and live recitation checks. Adults and children.',
        keywords: 'quran memorization online, hifz program, hifz teacher online, memorize quran, حفظ القرآن أونلاين, برنامج حفظ القرآن',
      },
      breadcrumb: 'Hifz program',
      hero: {
        badge: 'New portion, revision, review',
        title: 'Memorize the Quran with a teacher who keeps you on track',
        subtitle: 'Hifz is a daily habit more than a talent. Your teacher sets the portion, hears it, fixes it, and schedules the revision that stops it from slipping. Your Mushaf on Hudan shows every ayah you have memorized.',
        ctaPrimary: 'Start your Hifz journey',
        ctaSecondary: 'See Hifz teachers',
        trust: ['Personal daily and weekly plan', 'Revision built into every week', 'Digital Mushaf with hifz tracking', 'Suitable for adults and children'],
      },
      stats: {
        ...COMMON_EN,
        a: { value: '3 parts', label: 'New, recent, old: every week' },
        b: { value: '1 to 1', label: 'Session format' },
      },
      why: {
        eyebrow: 'Why memorize with Hudan',
        title: 'A method, not just a listener',
        items: [
          { title: 'Read before you memorize', desc: 'Teachers check your recitation of the portion first, so you memorize it correctly the first time instead of unlearning mistakes later.' },
          { title: 'The three-part week', desc: 'New portion, recent revision, and old revision are scheduled together. Old Hifz that is not revised is lost; the plan protects it.' },
          { title: 'A Mushaf that remembers', desc: 'Mark ayat as memorized, under review or mastered. Your teacher sees the same map and plans revision from it.' },
          { title: 'Realistic pacing', desc: 'Half a page, a page, or a few ayat a day. The right pace is the one you can revise, and your teacher adjusts it as you go.' },
          { title: 'Accountability that is kind', desc: 'A fixed session time and a teacher waiting for you is what keeps most people going through the hard middle juz.' },
          { title: 'Streaks and milestones', desc: 'Daily streaks, Juz milestones and certificates for completed portions keep the long road motivating.' },
        ],
      },
      tracks: {
        eyebrow: 'Choose a pace',
        title: 'Three paths through the Quran',
        subtitle: 'Your teacher will help you pick, and you can change pace at any time.',
        items: [
          { tag: 'Steady', title: 'A few ayat a day', bullets: ['Start with Juz Amma and short surahs', 'Ideal alongside work, study or school', 'Revision takes priority over speed'] },
          { tag: 'Committed', title: 'Half a page a day', bullets: ['Suits students with a daily practice window', 'Weekly consolidation of the week’s pages', 'Regular full-Juz recitations to the teacher'] },
          { tag: 'Intensive', title: 'A page or more a day', bullets: ['For those with dedicated time, or a Ramadan push', 'Daily sessions or a two-teacher schedule', 'Strict revision ratio to keep older Hifz strong'] },
        ],
      },
      teachers: { eyebrow: 'Teachers', title: 'Hifz teachers on Hudan', subtitle: 'Every teacher on this page lists Hifz or memorization as a specialty. Many are huffadh with an Ijazah.', viewAll: COMMON_EN.viewAll, empty: COMMON_EN.empty, error: COMMON_EN.error },
      courses: { eyebrow: 'Courses', title: 'Memorization courses and plans', viewAll: COMMON_EN.coursesViewAll },
      how: { eyebrow: COMMON_EN.howEyebrow, title: COMMON_EN.howTitle, items: COMMON_EN.how },
      faq: {
        eyebrow: COMMON_EN.faqEyebrow,
        title: COMMON_EN.faqTitle,
        items: [
          { q: 'How long does it take to memorize the whole Quran?', a: 'It depends on daily time and revision discipline. At a steady half page a day with revision, three to four years is common for adults; a page a day with strong revision can take under two. Most people underestimate revision, not memorization.' },
          { q: 'Do I need to know Tajweed first?', a: 'You need to read correctly. Teachers fix Tajweed as you go, but memorizing with major reading errors means memorizing the errors, so many start with a short Tajweed block.' },
          { q: 'Can my child join the program?', a: 'Yes. Create a parent account, add your child, and book with a teacher who lists children’s Hifz. See the Quran for Kids page for ages and pacing.' },
          { q: 'How do you handle revision?', a: 'Each week has new, recent and old portions. Your teacher listens to revision as well as new Hifz, and the Mushaf marks what is due for review.' },
          { q: 'What if I miss days?', a: 'The plan resumes from revision, not from new material. Streaks can be frozen for travel or illness so a break does not erase your progress.' },
          { q: 'Can I get an Ijazah after finishing?', a: 'Students who complete the Quran with strong Tajweed can continue with a sanad-holding teacher to recite the whole Quran for an Ijazah. See the Ijazah page.' },
        ],
      },
      cta: { title: 'The best time to start Hifz was years ago. The second best is this week.', subtitle: 'Book a trial with a Hifz teacher and leave it with a plan.', primary: 'Start your Hifz journey', secondary: COMMON_EN.ctaSecondary },
    },
    ar: {
      seo: {
        title: 'برنامج حفظ القرآن أونلاين مع معلم خاص | هُدًى للمتقين',
        description: 'احفظ القرآن مع معلم حفظ خاص: مقدار يومي، مراجعة منظمة، مصحف رقمي يتابع كل آية، وتسميع مباشر. للكبار والأطفال.',
        keywords: 'حفظ القرآن أونلاين, برنامج حفظ القرآن, معلم تحفيظ أونلاين, كيف أحفظ القرآن, حفظ القرآن في سنة, hifz program online',
      },
      breadcrumb: 'برنامج الحفظ',
      hero: {
        badge: 'جديد، قريب، بعيد',
        title: 'احفظ القرآن مع معلم يُبقيك على الطريق',
        subtitle: 'الحفظ عادة يومية أكثر منه موهبة. معلمك يحدد المقدار، ويسمعه منك، ويصححه، ويرتب المراجعة التي تمنع تفلّته. ومصحفك في هُدًى يُظهر كل آية حفظتها.',
        ctaPrimary: 'ابدأ رحلة الحفظ',
        ctaSecondary: 'شاهد معلمي التحفيظ',
        trust: ['خطة يومية وأسبوعية خاصة بك', 'المراجعة جزء من كل أسبوع', 'مصحف رقمي يتابع حفظك', 'يناسب الكبار والأطفال'],
      },
      stats: {
        ...COMMON_AR,
        a: { value: '3 أقسام', label: 'جديد وقريب وبعيد كل أسبوع' },
        b: { value: 'فردي', label: 'نمط الجلسة' },
      },
      why: {
        eyebrow: 'لماذا تحفظ مع هُدًى',
        title: 'منهج، لا مجرد مُستمِع',
        items: [
          { title: 'اقرأ قبل أن تحفظ', desc: 'يتحقق المعلم من تلاوتك للمقدار أولًا، فتحفظه صحيحًا من المرة الأولى بدل أن تعالج أخطاء الحفظ لاحقًا.' },
          { title: 'أسبوع بثلاثة أقسام', desc: 'المقدار الجديد، والمراجعة القريبة، والمراجعة البعيدة تُجدوَل معًا. الحفظ القديم الذي لا يُراجَع يضيع، والخطة تحميه.' },
          { title: 'مصحف يتذكر', desc: 'علّم الآيات: محفوظة، قيد المراجعة، متقنة. ويرى معلمك الخريطة نفسها ويخطط المراجعة منها.' },
          { title: 'وتيرة واقعية', desc: 'نصف صفحة، أو صفحة، أو بضع آيات في اليوم. الوتيرة الصحيحة هي التي تستطيع مراجعتها، ومعلمك يعدّلها معك.' },
          { title: 'التزام برفق', desc: 'موعد ثابت ومعلم ينتظرك هو ما يُبقي أكثر الناس مستمرين في الأجزاء الوسطى الصعبة.' },
          { title: 'سلاسل ومحطات', desc: 'سلاسل يومية، ومحطات عند كل جزء، وشهادات للأجزاء المكتملة تجعل الطريق الطويل محفزًا.' },
        ],
      },
      tracks: {
        eyebrow: 'اختر وتيرتك',
        title: 'ثلاثة طرق في القرآن',
        subtitle: 'يساعدك معلمك على الاختيار، ويمكنك تغيير الوتيرة متى شئت.',
        items: [
          { tag: 'متدرّج', title: 'بضع آيات يوميًا', bullets: ['ابدأ بجزء عمّ وقصار السور', 'مناسب مع العمل أو الدراسة', 'المراجعة مقدَّمة على السرعة'] },
          { tag: 'ملتزم', title: 'نصف صفحة يوميًا', bullets: ['لمن لديه وقت تدريب يومي', 'تثبيت أسبوعي لصفحات الأسبوع', 'تسميع جزء كامل للمعلم بانتظام'] },
          { tag: 'مكثّف', title: 'صفحة أو أكثر يوميًا', bullets: ['لمن يملك وقتًا مخصصًا أو في همّة رمضان', 'جلسات يومية أو معلمان بجدولين', 'نسبة مراجعة صارمة لتثبيت الحفظ القديم'] },
        ],
      },
      teachers: { eyebrow: 'المعلمون', title: 'معلمو التحفيظ في هُدًى', subtitle: 'كل معلم في هذه الصفحة يذكر الحفظ أو التحفيظ ضمن تخصصاته، وكثير منهم حفّاظ مُجازون.', viewAll: COMMON_AR.viewAll, empty: COMMON_AR.empty, error: COMMON_AR.error },
      courses: { eyebrow: 'الدورات', title: 'دورات وخطط الحفظ', viewAll: COMMON_AR.coursesViewAll },
      how: { eyebrow: COMMON_AR.howEyebrow, title: COMMON_AR.howTitle, items: COMMON_AR.how },
      faq: {
        eyebrow: COMMON_AR.faqEyebrow,
        title: COMMON_AR.faqTitle,
        items: [
          { q: 'كم يستغرق حفظ القرآن كاملًا؟', a: 'يعتمد على الوقت اليومي وانضباط المراجعة. بنصف صفحة يوميًا مع مراجعة، تشيع ثلاث إلى أربع سنوات للكبار، وبصفحة يوميًا مع مراجعة قوية قد يتم في أقل من سنتين. أكثر الناس يستهينون بالمراجعة لا بالحفظ.' },
          { q: 'هل يلزمني التجويد أولًا؟', a: 'يلزمك أن تقرأ قراءة صحيحة. يصحح المعلم التجويد أثناء الطريق، لكن الحفظ مع أخطاء كبيرة في القراءة يعني حفظ الأخطاء، لذا يبدأ كثيرون بجرعة قصيرة من التجويد.' },
          { q: 'هل يمكن لطفلي الانضمام؟', a: 'نعم. أنشئ حساب ولي أمر، وأضف طفلك، واحجز مع معلم يذكر تحفيظ الأطفال. راجع صفحة القرآن للأطفال للأعمار والوتيرة.' },
          { q: 'كيف تتعاملون مع المراجعة؟', a: 'كل أسبوع فيه مقدار جديد وقريب وبعيد. يستمع معلمك للمراجعة كما يستمع للحفظ الجديد، والمصحف يعلّم ما حان موعد مراجعته.' },
          { q: 'ماذا لو فاتتني أيام؟', a: 'تُستأنف الخطة من المراجعة لا من الجديد. ويمكن تجميد السلسلة للسفر أو المرض فلا يمحو الانقطاع تقدمك.' },
          { q: 'هل أحصل على إجازة بعد الختم؟', a: 'من يُتم القرآن بتجويد متقن يمكنه المتابعة مع معلم صاحب سند ليقرأ القرآن كاملًا للإجازة. راجع صفحة الإجازة.' },
        ],
      },
      cta: { title: 'أفضل وقت لبدء الحفظ كان قبل سنوات، وثاني أفضل وقت هو هذا الأسبوع', subtitle: 'احجز جلسة تجريبية مع معلم تحفيظ واخرج منها بخطة.', primary: 'ابدأ رحلة الحفظ', secondary: COMMON_AR.ctaSecondary },
    },
  },

  ijazah: {
    path: '/ijazah',
    teacherFilter: { specialty: 'Tajweed', ijazah_only: true },
    courseFilter: { category: 'Quran' },
    primaryHref: '/register/student',
    en: {
      seo: {
        title: 'Get an Ijazah in Quran Recitation Online | Hudan',
        description: 'Recite the Quran to a teacher with a connected sanad and earn an Ijazah in Hafs an Asim online. Prerequisites, the process, timelines and how to verify a teacher’s chain.',
        keywords: 'ijazah online, ijazah in quran, sanad quran, hafs an asim ijazah, إجازة في القرآن, الحصول على إجازة أونلاين',
      },
      breadcrumb: 'Ijazah',
      hero: {
        badge: 'A chain back to the Prophet ﷺ',
        title: 'Earn an Ijazah with a teacher whose sanad connects',
        subtitle: 'An Ijazah is a teacher’s testimony that you recited the whole Quran to them correctly, with their chain of transmission. On Hudan you recite to Ijazah-verified teachers, session by session, until the full recitation is complete.',
        ctaPrimary: 'Ask about an Ijazah track',
        ctaSecondary: 'See Ijazah-verified teachers',
        trust: ['Teachers with verified Ijazah documents', 'Hafs an Asim, other qiraat on request', 'Recitation by memory or by sight', 'Written Ijazah on completion'],
      },
      stats: {
        ...COMMON_EN,
        a: { value: 'Sanad', label: 'Verified chain of transmission' },
        b: { value: 'Muttasil', label: 'Connected, teacher to teacher' },
      },
      why: {
        eyebrow: 'What an Ijazah is',
        title: 'Not a certificate of attendance',
        items: [
          { title: 'Testimony, not a course', desc: 'An Ijazah says a specific teacher heard you recite the entire Quran and found it worthy of transmission. Nothing is issued for finishing lessons.' },
          { title: 'The sanad matters', desc: 'A sanad is the named chain from your teacher back to the Prophet ﷺ. On Hudan, teachers upload their Ijazah for verification before they can display the badge.' },
          { title: 'Two forms', desc: 'Ijazah by memory (hifz) for those who have memorized, and by sight (nazar) for those who recite from the Mushaf. Both require full, corrected recitation.' },
          { title: 'Tajweed at the level required', desc: 'Before the Ijazah recitation begins, your teacher will confirm your Tajweed is consistent. Many students spend a preparatory block on it first.' },
          { title: 'A realistic timeline', desc: 'Reciting the whole Quran at one to two sessions a week usually takes many months. Faster is possible with daily sessions and strong preparation.' },
          { title: 'You can verify', desc: 'Ask any teacher to show their sanad and the name of the teacher who granted it. Reputable teachers welcome the question.' },
        ],
      },
      tracks: {
        eyebrow: 'Which track',
        title: 'Three routes to an Ijazah',
        subtitle: 'Your teacher will assess you in a trial session and recommend one.',
        items: [
          { tag: 'Preparation', title: 'Polish Tajweed first', bullets: ['Full review of the rules of Tajweed', 'Recitation of selected surahs with correction', 'Entry into the Ijazah recitation when ready'] },
          { tag: 'Nazar', title: 'Recite the Mushaf by sight', bullets: ['Full Quran recited to the teacher from the Mushaf', 'Corrections logged per session', 'Ijazah in Hafs an Asim by sight on completion'] },
          { tag: 'Hifz', title: 'Recite from memory', bullets: ['For those who have completed memorization', 'Recitation of the entire Quran by heart', 'Ijazah by memory on completion'] },
        ],
      },
      teachers: { eyebrow: 'Teachers', title: 'Ijazah-verified teachers on Hudan', subtitle: 'Every teacher on this page has uploaded an Ijazah that was reviewed by the platform. Ask them about their sanad in the trial.', viewAll: COMMON_EN.viewAll, empty: COMMON_EN.empty, error: COMMON_EN.error },
      courses: { eyebrow: 'Courses', title: 'Recitation and Tajweed courses', viewAll: COMMON_EN.coursesViewAll },
      how: { eyebrow: COMMON_EN.howEyebrow, title: COMMON_EN.howTitle, items: COMMON_EN.how },
      faq: {
        eyebrow: COMMON_EN.faqEyebrow,
        title: COMMON_EN.faqTitle,
        items: [
          { q: 'Is an online Ijazah valid?', a: 'Yes, when the teacher hears your full recitation and holds a genuine connected sanad. What matters is the hearing and the chain, not the medium. Many well-known teachers now grant Ijazah after recitation over video.' },
          { q: 'Do I need to have memorized the Quran?', a: 'Not for an Ijazah by sight (nazar). You recite the whole Quran from the Mushaf. An Ijazah by memory requires full memorization.' },
          { q: 'How long does it take?', a: 'The full recitation is about 600 pages. At one to two pages per session, two sessions a week, expect well over a year; daily sessions shorten it considerably. Preparation time depends on your Tajweed.' },
          { q: 'How much does it cost?', a: 'You pay per session at your teacher’s hourly rate; there is no separate Ijazah fee on Hudan. The total depends on how many sessions the recitation takes.' },
          { q: 'How do I verify a teacher’s sanad?', a: 'Look for the verified badge on the profile, then ask the teacher for the name of their sheikh and the chain. A genuine sanad is a list of names you can ask about.' },
          { q: 'Does an Ijazah make me a scholar?', a: 'No. An Ijazah attests to your recitation of the Quran with Tajweed through a chain. It says nothing about knowledge of tafsir, fiqh or Arabic.' },
        ],
      },
      cta: { title: 'Join a chain that has never been broken', subtitle: 'Book a trial with an Ijazah-verified teacher and ask about the track that fits you.', primary: 'Ask about an Ijazah track', secondary: COMMON_EN.ctaSecondary },
    },
    ar: {
      seo: {
        title: 'الحصول على إجازة في القرآن أونلاين بسند متصل | هُدًى للمتقين',
        description: 'اقرأ القرآن كاملًا على معلم صاحب سند متصل واحصل على إجازة برواية حفص عن عاصم أونلاين. الشروط، والمسار، والمدة، وكيف تتحقق من سند المعلم.',
        keywords: 'إجازة في القرآن, الحصول على إجازة أونلاين, سند متصل, إجازة حفص عن عاصم, ijazah online',
      },
      breadcrumb: 'الإجازة',
      hero: {
        badge: 'سند يتصل بالنبي ﷺ',
        title: 'احصل على إجازة مع معلم سنده متصل',
        subtitle: 'الإجازة شهادة من معلمك بأنك قرأت عليه القرآن كاملًا قراءة صحيحة، بسنده المتصل. في هُدًى تقرأ على معلمين مُجازين موثَّقين، جلسة بعد جلسة، حتى تُتم القراءة.',
        ctaPrimary: 'اسأل عن مسار الإجازة',
        ctaSecondary: 'شاهد المعلمين المُجازين',
        trust: ['معلمون بوثائق إجازة موثّقة', 'حفص عن عاصم، وقراءات أخرى عند الطلب', 'قراءة غيبًا أو نظرًا', 'إجازة مكتوبة عند الإتمام'],
      },
      stats: {
        ...COMMON_AR,
        a: { value: 'السند', label: 'سلسلة رواية موثّقة' },
        b: { value: 'متصل', label: 'من معلم إلى معلم' },
      },
      why: {
        eyebrow: 'ما الإجازة',
        title: 'ليست شهادة حضور',
        items: [
          { title: 'شهادة لا دورة', desc: 'الإجازة تقول إن معلمًا بعينه سمعك تقرأ القرآن كله ووجدك أهلًا لنقله. ولا تُمنح لإتمام الدروس.' },
          { title: 'السند هو الأصل', desc: 'السند سلسلة الأسماء من معلمك إلى النبي ﷺ. وفي هُدًى يرفع المعلم إجازته للتوثيق قبل أن تظهر له شارة الإجازة.' },
          { title: 'صورتان', desc: 'إجازة غيبًا لمن أتم الحفظ، وإجازة نظرًا لمن يقرأ من المصحف. وكلتاهما تشترط قراءة كاملة مصححة.' },
          { title: 'تجويد بالمستوى المطلوب', desc: 'قبل بدء قراءة الإجازة يتأكد معلمك من ثبات تجويدك، وكثير من الطلاب يقضون مرحلة تهيئة أولًا.' },
          { title: 'مدة واقعية', desc: 'قراءة القرآن كاملًا بجلسة أو جلستين أسبوعيًا تستغرق أشهرًا كثيرة عادة. ويمكن أسرع من ذلك بجلسات يومية وتهيئة جيدة.' },
          { title: 'يمكنك التحقق', desc: 'اطلب من أي معلم أن يُريك سنده واسم من أجازه. والمعلمون الموثوقون يرحبون بالسؤال.' },
        ],
      },
      tracks: {
        eyebrow: 'أي مسار',
        title: 'ثلاثة طرق إلى الإجازة',
        subtitle: 'يقيّمك معلمك في جلسة تجريبية ويرشح لك أحدها.',
        items: [
          { tag: 'تهيئة', title: 'أتقن التجويد أولًا', bullets: ['مراجعة كاملة لأحكام التجويد', 'قراءة سور مختارة مع التصحيح', 'الدخول في قراءة الإجازة عند الجاهزية'] },
          { tag: 'نظرًا', title: 'اقرأ من المصحف', bullets: ['القرآن كاملًا على المعلم من المصحف', 'تدوين التصحيحات في كل جلسة', 'إجازة نظرًا برواية حفص عند الإتمام'] },
          { tag: 'غيبًا', title: 'اقرأ من حفظك', bullets: ['لمن أتم الحفظ', 'قراءة القرآن كاملًا غيبًا', 'إجازة غيبًا عند الإتمام'] },
        ],
      },
      teachers: { eyebrow: 'المعلمون', title: 'المعلمون المُجازون في هُدًى', subtitle: 'كل معلم في هذه الصفحة رفع إجازة راجعتها المنصة. اسأله عن سنده في الجلسة التجريبية.', viewAll: COMMON_AR.viewAll, empty: COMMON_AR.empty, error: COMMON_AR.error },
      courses: { eyebrow: 'الدورات', title: 'دورات التلاوة والتجويد', viewAll: COMMON_AR.coursesViewAll },
      how: { eyebrow: COMMON_AR.howEyebrow, title: COMMON_AR.howTitle, items: COMMON_AR.how },
      faq: {
        eyebrow: COMMON_AR.faqEyebrow,
        title: COMMON_AR.faqTitle,
        items: [
          { q: 'هل الإجازة عبر الإنترنت معتبرة؟', a: 'نعم، حين يسمع المعلم قراءتك كاملة ويكون له سند متصل حقيقي. العبرة بالسماع والسند لا بالوسيلة. وكثير من المشايخ المعروفين يجيزون اليوم بعد القراءة عبر الفيديو.' },
          { q: 'هل يلزم أن أكون حافظًا؟', a: 'لا للإجازة نظرًا، إذ تقرأ القرآن كاملًا من المصحف. أما الإجازة غيبًا فتشترط إتمام الحفظ.' },
          { q: 'كم تستغرق؟', a: 'القراءة الكاملة نحو 600 صفحة. بصفحة أو صفحتين في الجلسة وجلستين أسبوعيًا توقع أكثر من سنة، والجلسات اليومية تختصرها كثيرًا. ومدة التهيئة تعتمد على تجويدك.' },
          { q: 'كم التكلفة؟', a: 'تدفع لكل جلسة بسعر المعلم للساعة، ولا رسوم منفصلة للإجازة في هُدًى. والإجمالي يعتمد على عدد الجلسات التي تستغرقها القراءة.' },
          { q: 'كيف أتحقق من سند المعلم؟', a: 'ابحث عن شارة التوثيق في الملف، ثم اسأل المعلم عن اسم شيخه وسلسلة السند. السند الحقيقي قائمة أسماء يمكنك السؤال عنها.' },
          { q: 'هل تجعلني الإجازة عالمًا؟', a: 'لا. الإجازة تشهد لك بقراءة القرآن بالتجويد بسند، ولا تقول شيئًا عن علمك بالتفسير أو الفقه أو العربية.' },
        ],
      },
      cta: { title: 'انضم إلى سلسلة لم تنقطع', subtitle: 'احجز جلسة تجريبية مع معلم مُجاز واسأل عن المسار المناسب لك.', primary: 'اسأل عن مسار الإجازة', secondary: COMMON_AR.ctaSecondary },
    },
  },

  'quranic-arabic': {
    path: '/quranic-arabic',
    teacherFilter: { specialty: 'Arabic' },
    courseFilter: { category: 'Quranic Arabic' },
    primaryHref: '/register/student',
    en: {
      seo: {
        title: 'Learn Quranic Arabic Online, One to One | Hudan',
        description: 'Understand the Quran in its own language. Live Quranic Arabic lessons: vocabulary of the Quran, grammar you meet in the ayat, and reading with meaning, with teachers who teach in English.',
        keywords: 'quranic arabic online, learn arabic to understand quran, arabic for quran, classical arabic lessons, تعلم العربية لفهم القرآن, تعلم العربية أونلاين',
      },
      breadcrumb: 'Quranic Arabic',
      hero: {
        badge: 'Understand what you recite',
        title: 'Learn the Arabic of the Quran, ayah by ayah',
        subtitle: 'A few hundred words make up most of the Quran. Learn them where they occur, with the grammar you actually meet in the ayat, so recitation turns into understanding.',
        ctaPrimary: 'Start with a trial lesson',
        ctaSecondary: 'See Arabic teachers',
        trust: ['Lessons taught in English or Arabic', 'Vocabulary from the Quran itself', 'Grammar tied to real ayat', 'From complete beginner'],
      },
      stats: {
        ...COMMON_EN,
        a: { value: '80%', label: 'Of the Quran uses a core vocabulary you can learn' },
        b: { value: '1 to 1', label: 'Session format' },
      },
      why: {
        eyebrow: 'Why Quranic Arabic',
        title: 'Arabic in service of the Quran',
        items: [
          { title: 'Words where they live', desc: 'You learn a word in the ayah you recite in prayer, not from a list. Retention is far higher and every lesson has a payoff.' },
          { title: 'Grammar that earns its place', desc: 'Nahw and sarf are introduced as you meet them: the word forms, the cases, the particles that change meaning.' },
          { title: 'Start with prayer', desc: 'Al-Fatihah, the short surahs and the phrases of salah first, so understanding reaches your daily worship quickly.' },
          { title: 'Teachers who explain in English', desc: 'Many teachers teach through English, then move you gradually into Arabic explanation.' },
          { title: 'Reading and listening', desc: 'You read, translate aloud and listen to recitation with the text, so all three skills grow together.' },
          { title: 'Towards tafsir', desc: 'Students who finish the core can continue into simplified tafsir with the same teacher.' },
        ],
      },
      tracks: {
        eyebrow: 'Where to start',
        title: 'Three levels',
        subtitle: 'Your teacher will place you in a trial lesson.',
        items: [
          { tag: 'Foundations', title: 'I know the letters, little else', bullets: ['Core vocabulary of Juz Amma and salah', 'Nouns, verbs and particles in context', 'Reading short ayat with meaning'] },
          { tag: 'Building', title: 'I understand some words', bullets: ['Verb forms and patterns (sarf)', 'Sentence structure and cases (nahw) in the ayat', 'Longer surahs read with translation'] },
          { tag: 'Reading', title: 'I want to read the Quran with understanding', bullets: ['Guided reading through selected surahs', 'Classical grammar points as they occur', 'Introduction to simplified tafsir'] },
        ],
      },
      teachers: { eyebrow: 'Teachers', title: 'Arabic teachers on Hudan', subtitle: 'Every teacher on this page lists Arabic or Quranic Arabic as a specialty.', viewAll: COMMON_EN.viewAll, empty: COMMON_EN.empty, error: COMMON_EN.error },
      courses: { eyebrow: 'Courses', title: 'Quranic Arabic courses', viewAll: COMMON_EN.coursesViewAll },
      how: { eyebrow: COMMON_EN.howEyebrow, title: COMMON_EN.howTitle, items: COMMON_EN.how },
      faq: {
        eyebrow: COMMON_EN.faqEyebrow,
        title: COMMON_EN.faqTitle,
        items: [
          { q: 'Is Quranic Arabic different from modern Arabic?', a: 'Quranic (classical) Arabic shares the grammar of Modern Standard Arabic but its vocabulary and style are specific. Learning it directly is faster if understanding the Quran is your goal.' },
          { q: 'Do I need to read Arabic first?', a: 'You need to read the script, even slowly. If you cannot yet, teachers start with reading and move into vocabulary quickly.' },
          { q: 'How long until I understand my prayer?', a: 'Understanding Al-Fatihah, the short surahs and the phrases of salah usually comes within the first couple of months of weekly lessons.' },
          { q: 'Will I learn to speak Arabic?', a: 'The focus is reading and understanding the Quran. Some teachers add spoken practice if you ask.' },
          { q: 'Can I combine this with Tajweed or Hifz?', a: 'Yes. Many students take one Arabic session and one recitation session a week, sometimes with the same teacher.' },
          { q: 'Are there female teachers?', a: 'Yes. Use the gender filter on the teachers page.' },
        ],
      },
      cta: { title: 'Recite with your heart, not only your tongue', subtitle: 'Book a trial lesson and understand your first ayah this week.', primary: 'Start with a trial lesson', secondary: COMMON_EN.ctaSecondary },
    },
    ar: {
      seo: {
        title: 'تعلم العربية لفهم القرآن أونلاين فردًا لفرد | هُدًى للمتقين',
        description: 'افهم القرآن بلغته. دروس مباشرة في لغة القرآن: مفردات القرآن، والنحو والصرف كما يردان في الآيات، والقراءة بالفهم، مع معلمين يشرحون بالعربية أو الإنجليزية.',
        keywords: 'تعلم العربية لفهم القرآن, تعلم العربية أونلاين, تعلم العربية الفصحى للمبتدئين, لغة القرآن, quranic arabic online',
      },
      breadcrumb: 'لغة القرآن',
      hero: {
        badge: 'افهم ما تتلو',
        title: 'تعلّم عربية القرآن آيةً آية',
        subtitle: 'بضع مئات من الكلمات تشكّل معظم القرآن. تعلّمها في مواضعها، مع النحو الذي تلقاه فعلًا في الآيات، فتتحول التلاوة إلى فهم.',
        ctaPrimary: 'ابدأ بدرس تجريبي',
        ctaSecondary: 'شاهد معلمي العربية',
        trust: ['شرح بالعربية أو الإنجليزية', 'مفردات من القرآن نفسه', 'نحو مرتبط بآيات حقيقية', 'من الصفر تمامًا'],
      },
      stats: {
        ...COMMON_AR,
        a: { value: '80%', label: 'من القرآن بمفردات أساسية يمكنك تعلمها' },
        b: { value: 'فردي', label: 'نمط الجلسة' },
      },
      why: {
        eyebrow: 'لماذا لغة القرآن',
        title: 'عربية في خدمة القرآن',
        items: [
          { title: 'الكلمات في مواضعها', desc: 'تتعلم الكلمة في الآية التي تقرؤها في صلاتك لا من قائمة، فيرسخ الحفظ ويكون لكل درس ثمرة.' },
          { title: 'نحو يستحق موضعه', desc: 'يُقدَّم النحو والصرف حين تلقاهما: الأوزان، والإعراب، والأدوات التي تغيّر المعنى.' },
          { title: 'ابدأ بالصلاة', desc: 'الفاتحة وقصار السور وأذكار الصلاة أولًا، ليصل الفهم إلى عبادتك اليومية سريعًا.' },
          { title: 'معلمون يشرحون بالإنجليزية', desc: 'كثير من المعلمين يعلّمون عبر الإنجليزية ثم ينقلونك تدريجيًا إلى الشرح بالعربية.' },
          { title: 'قراءة واستماع', desc: 'تقرأ وتترجم بصوت عالٍ وتستمع للتلاوة مع النص، فتنمو المهارات الثلاث معًا.' },
          { title: 'نحو التفسير', desc: 'من يُتم الأساس يمكنه المتابعة إلى تفسير ميسّر مع المعلم نفسه.' },
        ],
      },
      tracks: {
        eyebrow: 'من أين تبدأ',
        title: 'ثلاثة مستويات',
        subtitle: 'يحدد معلمك مستواك في درس تجريبي.',
        items: [
          { tag: 'الأساس', title: 'أعرف الحروف ولا أكثر', bullets: ['المفردات الأساسية لجزء عمّ والصلاة', 'الاسم والفعل والحرف في السياق', 'قراءة آيات قصيرة بالمعنى'] },
          { tag: 'البناء', title: 'أفهم بعض الكلمات', bullets: ['أوزان الأفعال والصرف', 'تركيب الجملة والإعراب في الآيات', 'سور أطول تُقرأ مع الترجمة'] },
          { tag: 'القراءة', title: 'أريد قراءة القرآن بفهم', bullets: ['قراءة موجَّهة في سور مختارة', 'مسائل النحو الكلاسيكي حين ترد', 'مدخل إلى تفسير ميسّر'] },
        ],
      },
      teachers: { eyebrow: 'المعلمون', title: 'معلمو العربية في هُدًى', subtitle: 'كل معلم في هذه الصفحة يذكر العربية أو لغة القرآن ضمن تخصصاته.', viewAll: COMMON_AR.viewAll, empty: COMMON_AR.empty, error: COMMON_AR.error },
      courses: { eyebrow: 'الدورات', title: 'دورات لغة القرآن', viewAll: COMMON_AR.coursesViewAll },
      how: { eyebrow: COMMON_AR.howEyebrow, title: COMMON_AR.howTitle, items: COMMON_AR.how },
      faq: {
        eyebrow: COMMON_AR.faqEyebrow,
        title: COMMON_AR.faqTitle,
        items: [
          { q: 'هل عربية القرآن تختلف عن العربية الحديثة؟', a: 'تشترك العربية الفصحى القرآنية مع الفصحى الحديثة في النحو، لكن مفرداتها وأسلوبها خاصان. وتعلّمها مباشرة أسرع إن كان هدفك فهم القرآن.' },
          { q: 'هل يلزمني إتقان القراءة أولًا؟', a: 'يلزمك أن تقرأ الحروف ولو ببطء. وإن لم تكن تقرأ بعد فيبدأ المعلم بالقراءة ثم ينتقل سريعًا إلى المفردات.' },
          { q: 'متى أفهم صلاتي؟', a: 'فهم الفاتحة وقصار السور وأذكار الصلاة يأتي عادة خلال الشهرين الأولين من الدروس الأسبوعية.' },
          { q: 'هل سأتعلم التحدث بالعربية؟', a: 'التركيز على قراءة القرآن وفهمه. وبعض المعلمين يضيفون تدريب المحادثة إن طلبت.' },
          { q: 'هل أجمع بين هذا والتجويد أو الحفظ؟', a: 'نعم. كثير من الطلاب يأخذون جلسة عربية وجلسة تلاوة أسبوعيًا، وربما مع المعلم نفسه.' },
          { q: 'هل توجد معلمات؟', a: 'نعم. استخدم مرشح الجنس في صفحة المعلمين.' },
        ],
      },
      cta: { title: 'اتلُ بقلبك لا بلسانك فقط', subtitle: 'احجز درسًا تجريبيًا وافهم أول آية لك هذا الأسبوع.', primary: 'ابدأ بدرس تجريبي', secondary: COMMON_AR.ctaSecondary },
    },
  },

  'quran-for-adults': {
    path: '/quran-for-adults',
    teacherFilter: {},
    courseFilter: { level: 'Beginner' },
    primaryHref: '/register/student',
    en: {
      seo: {
        title: 'Quran Classes for Adults and Beginners Online | Hudan',
        description: 'It is never too late. Private online Quran classes for adults, complete beginners and new Muslims: reading from the letters up, Tajweed, memorization, at your pace and in your time zone.',
        keywords: 'quran classes for adults, learn quran as an adult, quran for beginners online, new muslim quran, تعلم القرآن للكبار, تعلم القرآن للمبتدئين',
      },
      breadcrumb: 'Quran for adults',
      hero: {
        badge: 'Beginners welcome',
        title: 'Learn the Quran as an adult, at your pace, without embarrassment',
        subtitle: 'Whether you never learned the letters, learned as a child and forgot, or embraced Islam recently, a private teacher meets you where you are. No classroom, no comparison, no judgment.',
        ctaPrimary: 'Book a trial session',
        ctaSecondary: 'See beginner-friendly teachers',
        trust: ['Complete beginners welcome', 'Sessions in English or Arabic', 'Private, one to one, on video', 'Evenings and weekends available'],
      },
      stats: {
        ...COMMON_EN,
        a: { value: 'Any age', label: 'Adults of every background' },
        b: { value: '30 min', label: 'Sessions from half an hour' },
      },
      why: {
        eyebrow: 'Why adults choose Hudan',
        title: 'Built for people with a life',
        items: [
          { title: 'Private by design', desc: 'One to one means nobody hears you struggle but your teacher, and your teacher is there precisely for that.' },
          { title: 'Start from the letters', desc: 'The Noorani Qaida takes adults from zero to reading in a few months. Many teachers have taught it to hundreds of adults.' },
          { title: 'Your schedule', desc: 'Early mornings, lunch breaks, after the children sleep. Teachers across time zones keep slots open when you are free.' },
          { title: 'Meaning alongside recitation', desc: 'Ask for meaning as you go; many teachers pair short surahs with their translation so worship deepens.' },
          { title: 'A plan that survives busy weeks', desc: 'Miss a week and the plan resumes from revision. Homework and recordings keep you moving between sessions.' },
          { title: 'Teachers of your choice', desc: 'Filter by gender, language and price. Read reviews from other adult learners before you book.' },
        ],
      },
      tracks: {
        eyebrow: 'Where to start',
        title: 'Three common starting points',
        subtitle: 'Tell your teacher which sounds like you.',
        items: [
          { tag: 'Zero', title: 'I do not know the letters', bullets: ['Arabic letters, sounds and harakat', 'Joining letters and reading words', 'Al-Fatihah and the short surahs for prayer'] },
          { tag: 'Rusty', title: 'I learned as a child and forgot', bullets: ['Quick refresh of reading and joining', 'Correcting habits with Tajweed basics', 'Building a daily recitation routine'] },
          { tag: 'New Muslim', title: 'I recently embraced Islam', bullets: ['What you need for salah first', 'Reading with meaning from the start', 'A patient pace with no assumptions'] },
        ],
      },
      teachers: { eyebrow: 'Teachers', title: 'Teachers who work well with adult beginners', subtitle: 'Top-rated teachers on Hudan. Use the filters on the full list for gender, language and price.', viewAll: COMMON_EN.viewAll, empty: COMMON_EN.empty, error: COMMON_EN.error },
      courses: { eyebrow: 'Courses', title: 'Beginner courses', viewAll: COMMON_EN.coursesViewAll },
      how: { eyebrow: COMMON_EN.howEyebrow, title: COMMON_EN.howTitle, items: COMMON_EN.how },
      faq: {
        eyebrow: COMMON_EN.faqEyebrow,
        title: COMMON_EN.faqTitle,
        items: [
          { q: 'Am I too old to learn to read the Quran?', a: 'No. Adults learn the script faster than children because they understand the system; the Noorani Qaida is designed for exactly this. Teachers regularly take adults in their fifties and sixties from zero to reading.' },
          { q: 'How long until I can read?', a: 'With two sessions a week and short daily practice, most adults read slowly but correctly within two to four months, then improve speed and Tajweed from there.' },
          { q: 'I am a new Muslim. Where do I start?', a: 'With what you need for prayer: Al-Fatihah, a few short surahs and the phrases of salah, alongside the letters. See the new Muslim guide on our blog.' },
          { q: 'Can I learn with an English-speaking teacher?', a: 'Yes. Filter the teacher list by language; many teachers explain in English.' },
          { q: 'How much does it cost?', a: 'Teachers set their own hourly rates; you pay per session. Many offer a trial session. See the pricing article on the blog for typical ranges.' },
          { q: 'Can I request a female or male teacher?', a: 'Yes. Use the gender filter on the teachers page or the female teachers page.' },
        ],
      },
      cta: { title: 'The Quran was sent for you too', subtitle: 'Book a trial session and read your first line this week.', primary: 'Book a trial session', secondary: COMMON_EN.ctaSecondary },
    },
    ar: {
      seo: {
        title: 'تعلم القرآن للكبار والمبتدئين أونلاين | هُدًى للمتقين',
        description: 'لم يفت الأوان. دروس قرآن خاصة أونلاين للكبار والمبتدئين تمامًا والمسلمين الجدد: القراءة من الحروف، والتجويد، والحفظ، بوتيرتك وفي وقتك.',
        keywords: 'تعلم القرآن للكبار, تعلم القرآن للمبتدئين, كيف أتعلم قراءة القرآن, تعليم القرآن للمسلم الجديد, quran classes for adults',
      },
      breadcrumb: 'القرآن للكبار',
      hero: {
        badge: 'المبتدئون مرحب بهم',
        title: 'تعلّم القرآن كبيرًا، بوتيرتك، وبلا حرج',
        subtitle: 'سواء لم تتعلم الحروف قط، أو تعلمتها صغيرًا ونسيت، أو أسلمت حديثًا، فمعلم خاص يلقاك حيث أنت. لا فصل، ولا مقارنة، ولا حكم.',
        ctaPrimary: 'احجز جلسة تجريبية',
        ctaSecondary: 'شاهد المعلمين المناسبين للمبتدئين',
        trust: ['المبتدئون تمامًا مرحب بهم', 'جلسات بالعربية أو الإنجليزية', 'خاصة، فردًا لفرد، عبر الفيديو', 'مواعيد مسائية وفي عطلة الأسبوع'],
      },
      stats: {
        ...COMMON_AR,
        a: { value: 'كل الأعمار', label: 'كبار من كل الخلفيات' },
        b: { value: '30 دقيقة', label: 'جلسات من نصف ساعة' },
      },
      why: {
        eyebrow: 'لماذا يختار الكبار هُدًى',
        title: 'مصمم لمن لديه حياة مشغولة',
        items: [
          { title: 'خصوصية بالتصميم', desc: 'فردًا لفرد يعني ألا يسمع تعثرك إلا معلمك، ومعلمك موجود لهذا تحديدًا.' },
          { title: 'ابدأ من الحروف', desc: 'القاعدة النورانية تنقل الكبار من الصفر إلى القراءة في أشهر قليلة، وكثير من المعلمين علّموها لمئات الكبار.' },
          { title: 'على جدولك', desc: 'الصباح الباكر، أو استراحة الغداء، أو بعد نوم الأطفال. معلمون في مناطق زمنية مختلفة يفتحون لك موعدًا حين تتفرغ.' },
          { title: 'المعنى مع التلاوة', desc: 'اطلب المعنى في أثناء الطريق، فكثير من المعلمين يقرنون قصار السور بترجمتها لتعمق العبادة.' },
          { title: 'خطة تصمد في الأسابيع المزدحمة', desc: 'إن فاتك أسبوع تُستأنف الخطة من المراجعة. والواجبات والتسجيلات تُبقيك متقدمًا بين الجلسات.' },
          { title: 'المعلم الذي تختاره', desc: 'صفِّ حسب الجنس واللغة والسعر، واقرأ تقييمات متعلمين كبار قبل الحجز.' },
        ],
      },
      tracks: {
        eyebrow: 'من أين تبدأ',
        title: 'ثلاث نقاط بداية شائعة',
        subtitle: 'أخبر معلمك أيها يشبه حالك.',
        items: [
          { tag: 'من الصفر', title: 'لا أعرف الحروف', bullets: ['الحروف العربية وأصواتها والحركات', 'وصل الحروف وقراءة الكلمات', 'الفاتحة وقصار السور للصلاة'] },
          { tag: 'نسيت', title: 'تعلمت صغيرًا ونسيت', bullets: ['تنشيط سريع للقراءة والوصل', 'تصحيح العادات بأساسيات التجويد', 'بناء ورد تلاوة يومي'] },
          { tag: 'مسلم جديد', title: 'أسلمت حديثًا', bullets: ['ما تحتاجه للصلاة أولًا', 'القراءة بالمعنى من البداية', 'وتيرة صبورة بلا افتراضات'] },
        ],
      },
      teachers: { eyebrow: 'المعلمون', title: 'معلمون يُحسنون مع المبتدئين الكبار', subtitle: 'أعلى المعلمين تقييمًا في هُدًى. استخدم مرشحات القائمة الكاملة للجنس واللغة والسعر.', viewAll: COMMON_AR.viewAll, empty: COMMON_AR.empty, error: COMMON_AR.error },
      courses: { eyebrow: 'الدورات', title: 'دورات للمبتدئين', viewAll: COMMON_AR.coursesViewAll },
      how: { eyebrow: COMMON_AR.howEyebrow, title: COMMON_AR.howTitle, items: COMMON_AR.how },
      faq: {
        eyebrow: COMMON_AR.faqEyebrow,
        title: COMMON_AR.faqTitle,
        items: [
          { q: 'هل كبرتُ على تعلم قراءة القرآن؟', a: 'لا. الكبار يتعلمون الحروف أسرع من الأطفال لأنهم يفهمون النظام، والقاعدة النورانية وُضعت لهذا. والمعلمون ينقلون كبارًا في الخمسينيات والستينيات من الصفر إلى القراءة بانتظام.' },
          { q: 'متى أستطيع القراءة؟', a: 'بجلستين أسبوعيًا وتدريب يومي قصير، يقرأ أغلب الكبار ببطء لكن بصحة خلال شهرين إلى أربعة، ثم تتحسن السرعة والتجويد بعدها.' },
          { q: 'أنا مسلم جديد، من أين أبدأ؟', a: 'بما تحتاجه للصلاة: الفاتحة وبضع سور قصيرة وأذكار الصلاة، مع الحروف. راجع دليل المسلم الجديد في مدونتنا.' },
          { q: 'هل أتعلم مع معلم يتحدث الإنجليزية؟', a: 'نعم. صفِّ قائمة المعلمين حسب اللغة، فكثير منهم يشرحون بالإنجليزية.' },
          { q: 'كم التكلفة؟', a: 'يحدد المعلمون أسعارهم للساعة، وتدفع لكل جلسة. وكثير منهم يقدم جلسة تجريبية. راجع مقال الأسعار في المدونة للنطاقات المعتادة.' },
          { q: 'هل أطلب معلمة أو معلمًا؟', a: 'نعم. استخدم مرشح الجنس في صفحة المعلمين أو صفحة المعلمات.' },
        ],
      },
      cta: { title: 'القرآن أُنزل لك أنت أيضًا', subtitle: 'احجز جلسة تجريبية واقرأ أول سطر لك هذا الأسبوع.', primary: 'احجز جلسة تجريبية', secondary: COMMON_AR.ctaSecondary },
    },
  },

  'female-quran-teachers': {
    path: '/female-quran-teachers',
    teacherFilter: { gender: 'Female' },
    courseFilter: {},
    primaryHref: '/register/student',
    en: {
      seo: {
        title: 'Female Quran Teachers Online for Women and Girls | Hudan',
        description: 'Learn Quran, Tajweed and Hifz with a female teacher online. Private sessions for women and girls with Ijazah-certified teachers, in English or Arabic, at times that suit you.',
        keywords: 'female quran teacher online, quran teacher for women, quran classes for girls, معلمة قرآن أونلاين, معلمة قرآن للبنات, معلمة تحفيظ',
      },
      breadcrumb: 'Female teachers',
      hero: {
        badge: 'For women and girls',
        title: 'Learn the Quran with a female teacher, in private',
        subtitle: 'Many women and parents of daughters prefer a female teacher. On Hudan you choose her yourself: read her profile and reviews, check her Ijazah, and book a private video session at a time that suits your home.',
        ctaPrimary: 'Book a trial session',
        ctaSecondary: 'See all female teachers',
        trust: ['Female teachers only on this page', 'Private, one to one, on video', 'Tajweed, Hifz, Arabic and children', 'Parent accounts for daughters'],
      },
      stats: {
        ...COMMON_EN,
        a: { value: 'Private', label: 'No group, no audience' },
        b: { value: 'Your choice', label: 'You pick the teacher, not us' },
      },
      why: {
        eyebrow: 'Why women choose Hudan',
        title: 'Comfort first, then everything else',
        items: [
          { title: 'You choose her', desc: 'Profiles, specialties, languages, rates and reviews are all visible before you book. Nobody is assigned to you.' },
          { title: 'Verified credentials', desc: 'Teachers showing the Ijazah badge uploaded their certificate for review. Ask about her sanad in the trial.' },
          { title: 'Private by default', desc: 'Sessions are one to one on video. You control your camera, and recordings are for you and your teacher only.' },
          { title: 'For your daughters too', desc: 'Create a parent account, add your daughter, and book with a teacher who lists children. You see progress and homework in one place.' },
          { title: 'Every specialty', desc: 'Reading from the letters, Tajweed, Hifz with revision, Quranic Arabic, and preparation for an Ijazah.' },
          { title: 'Hours that fit a household', desc: 'Teachers across time zones make mornings after school runs and late evenings possible.' },
        ],
      },
      tracks: {
        eyebrow: 'What you can learn',
        title: 'Three common goals',
        subtitle: 'Tell your teacher which one and the first session is planned around it.',
        items: [
          { tag: 'Recitation', title: 'Read correctly and beautifully', bullets: ['From the letters if needed, then Tajweed', 'Corrected recitation of the surahs you pray with', 'A daily recitation routine'] },
          { tag: 'Hifz', title: 'Memorize with a plan', bullets: ['New portion and revision every week', 'Digital Mushaf tracking every ayah', 'Juz milestones and certificates'] },
          { tag: 'Daughters', title: 'A teacher for my girl', bullets: ['Age-appropriate pace and lesson length', 'Parent sees homework and progress', 'Same teacher, week after week'] },
        ],
      },
      teachers: { eyebrow: 'Teachers', title: 'Female teachers on Hudan', subtitle: 'Every teacher on this page is a woman. Ratings come from students who took sessions.', viewAll: COMMON_EN.viewAll, empty: COMMON_EN.empty, error: COMMON_EN.error },
      courses: { eyebrow: 'Courses', title: 'Courses you can take with any teacher', viewAll: COMMON_EN.coursesViewAll },
      how: { eyebrow: COMMON_EN.howEyebrow, title: COMMON_EN.howTitle, items: COMMON_EN.how },
      faq: {
        eyebrow: COMMON_EN.faqEyebrow,
        title: COMMON_EN.faqTitle,
        items: [
          { q: 'Are all the teachers on this page women?', a: 'Yes. The list is filtered by the gender the teacher declared on her profile, and only approved teachers appear.' },
          { q: 'Do I have to turn my camera on?', a: 'No. You control your camera and microphone inside the session. Many students keep video off and recite by audio.' },
          { q: 'Can my daughter learn with a female teacher?', a: 'Yes. Register as a parent, add your daughter as a child profile, and book with a female teacher who lists children. Children do not get their own login; you manage everything.' },
          { q: 'Do female teachers grant Ijazah?', a: 'Teachers with the verified Ijazah badge hold a sanad and can take students through an Ijazah recitation. See the Ijazah page.' },
          { q: 'Which languages do they teach in?', a: 'Arabic and English are the most common; each profile lists the teacher’s teaching languages.' },
          { q: 'What does it cost?', a: 'Each teacher sets her own hourly rate, shown on her profile. Many offer a trial session.' },
        ],
      },
      cta: { title: 'A teacher you are comfortable with makes all the difference', subtitle: 'Book a trial session with a female teacher today.', primary: 'Book a trial session', secondary: COMMON_EN.ctaSecondary },
    },
    ar: {
      seo: {
        title: 'معلمة قرآن أونلاين للنساء والبنات | هُدًى للمتقين',
        description: 'تعلّمي القرآن والتجويد والحفظ مع معلمة أونلاين. جلسات خاصة للنساء والبنات مع معلمات مُجازات، بالعربية أو الإنجليزية، في الأوقات التي تناسبك.',
        keywords: 'معلمة قرآن أونلاين, معلمة قرآن للبنات, معلمة تحفيظ, معلمة تجويد, female quran teacher online',
      },
      breadcrumb: 'المعلمات',
      hero: {
        badge: 'للنساء والبنات',
        title: 'تعلّمي القرآن مع معلمة، في خصوصية تامة',
        subtitle: 'كثير من النساء وأولياء أمور البنات يفضلون معلمة. في هُدًى تختارينها بنفسك: اقرئي ملفها وتقييماتها، وتحققي من إجازتها، واحجزي جلسة فيديو خاصة في وقت يناسب بيتك.',
        ctaPrimary: 'احجزي جلسة تجريبية',
        ctaSecondary: 'شاهدي كل المعلمات',
        trust: ['معلمات فقط في هذه الصفحة', 'خاصة، فردًا لفرد، عبر الفيديو', 'تجويد وحفظ وعربية وأطفال', 'حسابات ولي أمر للبنات'],
      },
      stats: {
        ...COMMON_AR,
        a: { value: 'خاصة', label: 'بلا مجموعة ولا حضور' },
        b: { value: 'اختيارك', label: 'أنت تختارين المعلمة لا نحن' },
      },
      why: {
        eyebrow: 'لماذا تختار النساء هُدًى',
        title: 'الراحة أولًا، ثم كل شيء',
        items: [
          { title: 'أنت تختارينها', desc: 'الملفات والتخصصات واللغات والأسعار والتقييمات كلها ظاهرة قبل الحجز. لا أحد يُعيَّن لك.' },
          { title: 'مؤهلات موثّقة', desc: 'المعلمات اللواتي تظهر لهن شارة الإجازة رفعن شهادتهن للمراجعة. اسأليها عن سندها في الجلسة التجريبية.' },
          { title: 'خصوصية افتراضية', desc: 'الجلسات فردية عبر الفيديو. أنت تتحكمين بالكاميرا، والتسجيلات لك ولمعلمتك فقط.' },
          { title: 'ولبناتك أيضًا', desc: 'أنشئي حساب ولي أمر، وأضيفي ابنتك، واحجزي مع معلمة تذكر تعليم الأطفال. وترين التقدم والواجبات في مكان واحد.' },
          { title: 'كل التخصصات', desc: 'القراءة من الحروف، والتجويد، والحفظ مع المراجعة، ولغة القرآن، والتهيئة للإجازة.' },
          { title: 'أوقات تناسب البيت', desc: 'معلمات في مناطق زمنية مختلفة يجعلن الصباح بعد المدرسة والمساء المتأخر ممكنًا.' },
        ],
      },
      tracks: {
        eyebrow: 'ما يمكنك تعلمه',
        title: 'ثلاثة أهداف شائعة',
        subtitle: 'أخبري معلمتك بهدفك وتُخطَّط الجلسة الأولى عليه.',
        items: [
          { tag: 'التلاوة', title: 'أقرأ صحيحًا وجميلًا', bullets: ['من الحروف إن لزم ثم التجويد', 'تصحيح تلاوة السور التي تصلين بها', 'ورد تلاوة يومي'] },
          { tag: 'الحفظ', title: 'أحفظ بخطة', bullets: ['مقدار جديد ومراجعة كل أسبوع', 'مصحف رقمي يتابع كل آية', 'محطات الأجزاء وشهادات'] },
          { tag: 'البنات', title: 'معلمة لابنتي', bullets: ['وتيرة ومدة تناسب عمرها', 'ولي الأمر يرى الواجبات والتقدم', 'المعلمة نفسها أسبوعًا بعد أسبوع'] },
        ],
      },
      teachers: { eyebrow: 'المعلمات', title: 'المعلمات في هُدًى', subtitle: 'كل معلمة في هذه الصفحة امرأة، والتقييمات من طالبات أخذن جلسات فعلًا.', viewAll: COMMON_AR.viewAll, empty: COMMON_AR.empty, error: COMMON_AR.error },
      courses: { eyebrow: 'الدورات', title: 'دورات يمكنك أخذها مع أي معلمة', viewAll: COMMON_AR.coursesViewAll },
      how: { eyebrow: COMMON_AR.howEyebrow, title: COMMON_AR.howTitle, items: COMMON_AR.how },
      faq: {
        eyebrow: COMMON_AR.faqEyebrow,
        title: COMMON_AR.faqTitle,
        items: [
          { q: 'هل كل المعلمين في هذه الصفحة نساء؟', a: 'نعم. القائمة مرشَّحة حسب الجنس الذي أعلنته المعلمة في ملفها، ولا تظهر إلا المعلمات المعتمدات.' },
          { q: 'هل يلزم فتح الكاميرا؟', a: 'لا. أنت تتحكمين بالكاميرا والميكروفون داخل الجلسة، وكثيرات يبقين الفيديو مغلقًا ويتلون بالصوت.' },
          { q: 'هل تتعلم ابنتي مع معلمة؟', a: 'نعم. سجّلي كولي أمر، وأضيفي ابنتك كملف طفل، واحجزي مع معلمة تذكر تعليم الأطفال. الأطفال لا يملكون حساب دخول، وأنت تديرين كل شيء.' },
          { q: 'هل تمنح المعلمات إجازة؟', a: 'المعلمات اللواتي يحملن شارة الإجازة الموثّقة لديهن سند ويمكنهن إتمام قراءة الإجازة معك. راجعي صفحة الإجازة.' },
          { q: 'بأي لغات يُدرّسن؟', a: 'العربية والإنجليزية الأكثر شيوعًا، ويذكر كل ملف لغات تدريس المعلمة.' },
          { q: 'كم التكلفة؟', a: 'كل معلمة تحدد سعرها للساعة ويظهر في ملفها، وكثيرات يقدمن جلسة تجريبية.' },
        ],
      },
      cta: { title: 'المعلمة التي ترتاحين لها تصنع الفرق كله', subtitle: 'احجزي جلسة تجريبية مع معلمة اليوم.', primary: 'احجزي جلسة تجريبية', secondary: COMMON_AR.ctaSecondary },
    },
  },

  'online-quran-classes': {
    path: '/online-quran-classes',
    teacherFilter: {},
    courseFilter: { category: 'Quran' },
    primaryHref: '/register/student',
    en: {
      seo: {
        title: 'Online Quran Classes, One to One with Verified Teachers | Hudan',
        description: 'Live one-to-one online Quran classes with verified teachers: reading from zero, Tajweed correction, Hifz and Quranic Arabic, in English or Arabic, at times that fit your week.',
        keywords: 'online quran classes, learn quran online, online quran tutor, quran lessons online, دروس قرآن أونلاين, تعليم القرآن عن بعد',
      },
      breadcrumb: 'Online Quran classes',
      hero: {
        badge: 'Live, one to one, on video',
        title: 'Online Quran classes with a teacher who hears every word',
        subtitle: 'Not a recorded course. A live teacher listens to you recite, corrects you in the moment, sets homework and tracks your progress week by week — from your first letters to a polished recitation.',
        ctaPrimary: 'Start with a trial session',
        ctaSecondary: 'Browse all teachers',
        trust: ['Verified teachers with Ijazah badges', 'Explanation in English or Arabic', 'Male and female teachers', 'Try a session before you commit'],
      },
      stats: {
        ...COMMON_EN,
        a: { value: '1 to 1', label: 'Every session' },
        b: { value: '30-60 min', label: 'Typical session length' },
      },
      why: {
        eyebrow: 'Why learn this way',
        title: 'What a live class gives you that an app cannot',
        items: [
          { title: 'Correction in the moment', desc: 'You recite, the teacher stops you at the exact letter. Errors in pronunciation sound correct from the inside, which is why a listener is not optional.' },
          { title: 'You choose the teacher', desc: 'Filter by specialty, language, price and gender, read reviews from real students, and change teacher if the fit is wrong.' },
          { title: 'One path, any level', desc: 'Absolute beginner, returning after years, or preparing for an Ijazah — the plan is set in your first session, not bought off a shelf.' },
          { title: 'Times that fit your week', desc: 'Teachers across time zones keep slots open early, late and at weekends, so the appointment survives a busy month.' },
          { title: 'Everything in one place', desc: 'Homework with teacher grading, session notes, recordings where the teacher enables them, and progress you can actually see.' },
          { title: 'Clear pricing', desc: 'Each teacher lists an hourly rate and, where offered, a trial price. Prices display in your currency; charges are processed in US dollars.' },
        ],
      },
      tracks: {
        eyebrow: 'Where to start',
        title: 'Three ways people begin',
        subtitle: 'Tell your teacher which one describes you and the first session is planned around it.',
        items: [
          { tag: 'From zero', title: 'I cannot read Arabic yet', bullets: ['Letters, vowels and joining with the Noorani Qaida', 'Correct pronunciation from the first lesson', 'Short surahs read from the mushaf within weeks'] },
          { tag: 'Correction', title: 'I read, but I make mistakes', bullets: ['Makharij and the attributes of the letters', 'Rules of noon, meem, madd and stopping', 'Full surahs recited with detailed feedback'] },
          { tag: 'Memorisation', title: 'I want to memorise', bullets: ['A daily portion sized to your real week', 'A revision rotation so it stays memorised', 'A route towards an Ijazah with the same teacher'] },
        ],
      },
      teachers: { eyebrow: 'Teachers', title: 'Teachers you can book today', subtitle: 'Every teacher here has been reviewed before being listed. Ratings come from students who took sessions.', viewAll: COMMON_EN.viewAll, empty: COMMON_EN.empty, error: COMMON_EN.error },
      courses: { eyebrow: 'Courses', title: 'Structured Quran courses', viewAll: COMMON_EN.coursesViewAll },
      how: { eyebrow: COMMON_EN.howEyebrow, title: COMMON_EN.howTitle, items: COMMON_EN.how },
      faq: {
        eyebrow: COMMON_EN.faqEyebrow,
        title: COMMON_EN.faqTitle,
        items: [
          { q: 'What happens in an online Quran class?', a: 'You meet your teacher on video at the time you booked. You recite, the teacher corrects, explains what to fix and sets homework for the week. Sessions are usually 30 or 60 minutes, one to one.' },
          { q: 'Do I need to know Arabic to start?', a: 'No. Many students begin without recognising a single letter. Teachers who explain in English take you through the alphabet, the vowels and joining before any Tajweed rules are introduced.' },
          { q: 'How much do online Quran classes cost?', a: 'Each teacher sets their own hourly rate, which is shown on their profile, and many offer a discounted or free trial session. Prices are displayed in your chosen currency.' },
          { q: 'What software do I need?', a: 'A browser, a stable connection and a headset with a microphone. Sessions run on video from your dashboard, and audio quality matters far more than video quality.' },
          { q: 'Can my child take classes?', a: 'Yes. Parents can create a family account, add each child and choose a teacher who specialises in teaching children.' },
          { q: 'Are female teachers available?', a: 'Yes. You can filter the teacher list by gender, or start from the female teachers page.' },
        ],
      },
      cta: { title: 'Your first session can be this week', subtitle: 'Create a free account, pick a teacher, and book a time that suits you.', primary: 'Start with a trial session', secondary: COMMON_EN.ctaSecondary },
    },
    ar: {
      seo: {
        title: 'دروس قرآن أونلاين فردية مع معلمين موثّقين | هُدًى للمتقين',
        description: 'دروس قرآن مباشرة فردًا لفرد مع معلمين موثّقين: القراءة من الصفر، وتصحيح التجويد، والحفظ، والعربية القرآنية، بالعربية أو الإنجليزية، في أوقات تناسب أسبوعك.',
        keywords: 'دروس قرآن أونلاين, تعليم القرآن عن بعد, معلم قرآن أونلاين, تحفيظ قرآن أونلاين, online quran classes',
      },
      breadcrumb: 'دروس القرآن أونلاين',
      hero: {
        badge: 'مباشر، فردًا لفرد، عبر الفيديو',
        title: 'دروس قرآن أونلاين مع معلم يسمع كل كلمة',
        subtitle: 'ليست دورة مسجلة. معلم مباشر يستمع إلى تلاوتك ويصحح لك في حينها، ويكلّفك واجبًا، ويتابع تقدمك أسبوعًا بأسبوع — من أول حروفك إلى تلاوة متقنة.',
        ctaPrimary: 'ابدأ بجلسة تجريبية',
        ctaSecondary: 'تصفّح كل المعلمين',
        trust: ['معلمون موثّقون بشارات الإجازة', 'الشرح بالعربية أو الإنجليزية', 'معلمون ومعلمات', 'جرّب جلسة قبل الالتزام'],
      },
      stats: {
        ...COMMON_AR,
        a: { value: 'فردي', label: 'كل الجلسات' },
        b: { value: '30-60 دقيقة', label: 'مدة الجلسة المعتادة' },
      },
      why: {
        eyebrow: 'لماذا هذا الطريق',
        title: 'ما تمنحه الجلسة المباشرة ولا يمنحه التطبيق',
        items: [
          { title: 'تصحيح في حينه', desc: 'أنت تتلو والمعلم يوقفك عند الحرف بعينه. وخطأ النطق يبدو صحيحًا لصاحبه، ولهذا لم يكن السامع أمرًا اختياريًا.' },
          { title: 'أنت تختار المعلم', desc: 'صفِّ حسب التخصص واللغة والسعر والجنس، واقرأ تقييمات طلاب حقيقيين، وغيّر المعلم إن لم تناسبك الطريقة.' },
          { title: 'مسار واحد لكل مستوى', desc: 'مبتدئ تمامًا، أو عائد بعد سنين، أو مستعدّ للإجازة — الخطة تُوضع في جلستك الأولى لا تُشترى جاهزة.' },
          { title: 'أوقات تناسب أسبوعك', desc: 'معلمون في مناطق زمنية مختلفة يفتحون مواعيد باكرًا ومتأخرًا وفي العطلات، فيبقى الموعد قائمًا في الشهر المزدحم.' },
          { title: 'كل شيء في مكان واحد', desc: 'واجبات يقيّمها المعلم، وملاحظات الجلسة، وتسجيلات حين يفعّلها المعلم، وتقدّم تراه بعينك.' },
          { title: 'أسعار واضحة', desc: 'كل معلم يذكر أجره بالساعة وسعر الجلسة التجريبية إن قدّمها. وتُعرض الأسعار بعملتك، والتحصيل بالدولار الأمريكي.' },
        ],
      },
      tracks: {
        eyebrow: 'من أين تبدأ',
        title: 'ثلاث بدايات',
        subtitle: 'أخبر معلمك أيها يصفك، وتُخطَّط الجلسة الأولى على أساسه.',
        items: [
          { tag: 'من الصفر', title: 'لا أقرأ العربية بعد', bullets: ['الحروف والحركات والوصل بالقاعدة النورانية', 'النطق الصحيح من الدرس الأول', 'قراءة قصار السور من المصحف خلال أسابيع'] },
          { tag: 'تصحيح', title: 'أقرأ لكني أخطئ', bullets: ['مخارج الحروف وصفاتها', 'أحكام النون والميم والمدود والوقف', 'تلاوة سور كاملة بملاحظات تفصيلية'] },
          { tag: 'حفظ', title: 'أريد أن أحفظ', bullets: ['ورد يومي بمقدار أسبوعك الحقيقي', 'دورة مراجعة يبقى بها المحفوظ', 'طريق إلى الإجازة مع المعلم نفسه'] },
        ],
      },
      teachers: { eyebrow: 'المعلمون', title: 'معلمون تحجز معهم اليوم', subtitle: 'كل معلم هنا روجع قبل إدراجه، والتقييمات من طلاب أخذوا جلسات فعلًا.', viewAll: COMMON_AR.viewAll, empty: COMMON_AR.empty, error: COMMON_AR.error },
      courses: { eyebrow: 'الدورات', title: 'دورات قرآن منظمة', viewAll: COMMON_AR.coursesViewAll },
      how: { eyebrow: COMMON_AR.howEyebrow, title: COMMON_AR.howTitle, items: COMMON_AR.how },
      faq: {
        eyebrow: COMMON_AR.faqEyebrow,
        title: COMMON_AR.faqTitle,
        items: [
          { q: 'ماذا يحدث في درس القرآن أونلاين؟', a: 'تلتقي معلمك عبر الفيديو في الموعد الذي حجزته، فتتلو ويصحح لك، ويبيّن ما يُصلح، ويكلّفك واجبًا للأسبوع. والجلسة عادة ثلاثون أو ستون دقيقة، فردًا لفرد.' },
          { q: 'هل يلزمني إتقان العربية للبدء؟', a: 'لا. كثير من الطلاب يبدؤون ولا يعرفون حرفًا واحدًا. والمعلمون الذين يشرحون بالإنجليزية يأخذونك في الحروف والحركات والوصل قبل أي حكم من أحكام التجويد.' },
          { q: 'كم تكلفة دروس القرآن أونلاين؟', a: 'كل معلم يحدد أجره بالساعة وهو ظاهر في ملفه، وكثيرون يقدمون جلسة تجريبية مخفَّضة أو مجانية. وتُعرض الأسعار بالعملة التي تختارها.' },
          { q: 'ما البرامج التي أحتاجها؟', a: 'متصفح واتصال مستقر وسماعة بميكروفون. وتجري الجلسات بالفيديو من لوحتك، وجودة الصوت أهم بكثير من جودة الصورة.' },
          { q: 'هل يمكن لطفلي أن يدرس؟', a: 'نعم. يستطيع الوالد إنشاء حساب عائلي وإضافة كل طفل واختيار معلم متخصص في تعليم الأطفال.' },
          { q: 'هل تتوفر معلمات؟', a: 'نعم. يمكنك تصفية قائمة المعلمين حسب الجنس، أو البدء من صفحة المعلمات.' },
        ],
      },
      cta: { title: 'جلستك الأولى يمكن أن تكون هذا الأسبوع', subtitle: 'أنشئ حسابًا مجانيًا، واختر معلمك، واحجز وقتًا يناسبك.', primary: 'ابدأ بجلسة تجريبية', secondary: COMMON_AR.ctaSecondary },
    },
  },

  'quran-classes-for-beginners': {
    path: '/quran-classes-for-beginners',
    teacherFilter: {},
    courseFilter: { level: 'Beginner' },
    primaryHref: '/register/student',
    en: {
      seo: {
        title: 'Quran Classes for Beginners: Start From Zero | Hudan',
        description: 'Beginner Quran classes for adults and children who cannot read Arabic yet. Letters, vowels and joining with a live teacher, then your first surahs from the mushaf.',
        keywords: 'quran classes for beginners, learn to read quran, quran for beginners adults, beginner quran lessons, تعليم القرآن للمبتدئين, تعلم قراءة القرآن',
      },
      breadcrumb: 'Beginner classes',
      hero: {
        badge: 'No prior Arabic needed',
        title: 'Start from the first letter, with someone listening',
        subtitle: 'Beginner classes for adults and children who cannot yet read a word of Arabic. Twenty minutes a day and one live session a week is enough to be reading from the mushaf within months.',
        ctaPrimary: 'Book a first session',
        ctaSecondary: 'See beginner-friendly teachers',
        trust: ['Start with zero Arabic', 'Explanation in English or Arabic', 'Patient teachers for adults and children', 'No card needed to look around'],
      },
      stats: {
        ...COMMON_EN,
        a: { value: '28', label: 'Letters to learn first' },
        b: { value: '20 min', label: 'Daily practice we suggest' },
      },
      why: {
        eyebrow: 'Why beginners succeed here',
        title: 'Built for the first hundred days',
        items: [
          { title: 'Nobody is behind', desc: 'Adults who never learned as children are the most common beginner on this platform. Teachers here expect it and plan for it.' },
          { title: 'Sound before rules', desc: 'You learn to produce the letters correctly before any rule is named, so the rules later land on sounds you can already make.' },
          { title: 'A proven order', desc: 'The Noorani Qaida sequence — letters, shapes, vowels, sukoon, shaddah, madd — because it is the order that builds fastest.' },
          { title: 'Small steps, weekly checks', desc: 'A short daily practice, then a live session where a teacher hears you and fixes what a recording never could.' },
          { title: 'No transliteration', desc: 'Latin letters cannot represent the Arabic sounds. You read Arabic from the first week, slowly, and it pays for itself within a month.' },
          { title: 'A first surah, soon', desc: 'Most beginners are reading short surahs from the mushaf within two to three months of steady practice.' },
        ],
      },
      tracks: {
        eyebrow: 'Who this is for',
        title: 'Three kinds of beginner',
        subtitle: 'The starting point is the same; the pace and the tone are not.',
        items: [
          { tag: 'Adults', title: 'I never learned as a child', bullets: ['Letters and vowels at an adult pace', 'Explanation in English if you prefer', 'Reading a page of Juz Amma as the first milestone'] },
          { tag: 'Children', title: 'My child is starting out', bullets: ['Short sessions with a teacher used to children', 'Repetition, praise and a visible tracker', 'Parent updates after each session'] },
          { tag: 'Returning', title: 'I learned once and forgot', bullets: ['A quick assessment of what is still there', 'Correction of errors learned in childhood', 'Straight into reading rather than starting over'] },
        ],
      },
      teachers: { eyebrow: 'Teachers', title: 'Teachers who take beginners', subtitle: 'Filter by language on the full list if you want the explanation in English.', viewAll: COMMON_EN.viewAll, empty: COMMON_EN.empty, error: COMMON_EN.error },
      courses: { eyebrow: 'Courses', title: 'Beginner courses', viewAll: COMMON_EN.coursesViewAll },
      how: { eyebrow: COMMON_EN.howEyebrow, title: COMMON_EN.howTitle, items: COMMON_EN.how },
      faq: {
        eyebrow: COMMON_EN.faqEyebrow,
        title: COMMON_EN.faqTitle,
        items: [
          { q: 'Can I start if I cannot read Arabic at all?', a: 'Yes — that is exactly who these classes are for. You begin with the 28 letters and their sounds, then the vowel marks, then joining letters into words.' },
          { q: 'How long until I can read the Quran?', a: 'With twenty minutes of daily practice and a weekly session, most adults recognise all the letters within two weeks and read simple vowelled words within four to six weeks. Reading short surahs from the mushaf typically takes two to three months.' },
          { q: 'Am I too old to start?', a: 'No. Adults learn the letters more slowly than children but understand structure faster, and they revise more reliably. Many students on this platform started in their forties and fifties.' },
          { q: 'Do you teach with the Noorani Qaida?', a: 'Most teachers use it or follow its sequence, because it presents the letters, shapes, vowels and joins in the order that builds fastest. Ask your teacher in the first session.' },
          { q: 'I am embarrassed to recite in front of someone. Can I still learn?', a: 'This is the most common thing beginners say, and teachers hear it every week. Sessions are private, one to one, and the teacher is listening for what to fix rather than judging.' },
          { q: 'How often should I take classes?', a: 'One or two sessions a week with short daily practice in between works for almost everyone. Practice between sessions matters more than the number of sessions.' },
        ],
      },
      cta: { title: 'The first letter is the hardest part', subtitle: 'Book one session and let a teacher plan the rest with you.', primary: 'Book a first session', secondary: COMMON_EN.ctaSecondary },
    },
    ar: {
      seo: {
        title: 'دروس القرآن للمبتدئين: ابدأ من الصفر | هُدًى للمتقين',
        description: 'دروس للمبتدئين من الكبار والصغار ممن لا يقرؤون العربية بعد: الحروف والحركات والوصل مع معلم مباشر، ثم أول سورك من المصحف.',
        keywords: 'تعليم القرآن للمبتدئين, تعلم قراءة القرآن, دروس قرآن للكبار المبتدئين, القاعدة النورانية, quran for beginners',
      },
      breadcrumb: 'دروس المبتدئين',
      hero: {
        badge: 'لا يُشترط أي مستوى في العربية',
        title: 'ابدأ من الحرف الأول، وأحدٌ يسمعك',
        subtitle: 'دروس للمبتدئين من الكبار والصغار ممن لا يقرؤون كلمة عربية بعد. عشرون دقيقة يوميًا وجلسة مباشرة أسبوعيًا تكفي لتقرأ من المصحف خلال شهور.',
        ctaPrimary: 'احجز جلستك الأولى',
        ctaSecondary: 'شاهد معلمي المبتدئين',
        trust: ['ابدأ ولو لم تعرف حرفًا', 'الشرح بالعربية أو الإنجليزية', 'معلمون صبورون للكبار والصغار', 'التصفح بلا بطاقة دفع'],
      },
      stats: {
        ...COMMON_AR,
        a: { value: '28', label: 'حرفًا تتعلمها أولًا' },
        b: { value: '20 دقيقة', label: 'التدريب اليومي المقترح' },
      },
      why: {
        eyebrow: 'لماذا ينجح المبتدئ هنا',
        title: 'مبنيّ لأول مئة يوم',
        items: [
          { title: 'لا أحد متأخر', desc: 'الكبار الذين لم يتعلموا صغارًا هم أكثر المبتدئين في هذه المنصة، والمعلمون هنا يتوقعون ذلك ويخططون له.' },
          { title: 'الصوت قبل الأحكام', desc: 'تتعلم إخراج الحروف صحيحة قبل أن يُسمّى لك حكم، فتقع الأحكام بعد ذلك على أصوات تُحسنها.' },
          { title: 'ترتيب مجرَّب', desc: 'مسار القاعدة النورانية: الحروف، فالأشكال، فالحركات، فالسكون والشدة والمد؛ لأنه الترتيب الذي يبني أسرع.' },
          { title: 'خطوات صغيرة ومتابعة أسبوعية', desc: 'تدريب يومي قصير، ثم جلسة مباشرة يسمعك فيها معلم ويصحح ما لا يصححه تسجيل.' },
          { title: 'بلا كتابة لاتينية', desc: 'الحروف اللاتينية لا تمثّل أصوات العربية. تقرأ العربية من الأسبوع الأول ولو ببطء، ويعود ذلك عليك خلال شهر.' },
          { title: 'أول سورة قريبًا', desc: 'أكثر المبتدئين يقرؤون قصار السور من المصحف خلال شهرين إلى ثلاثة من التدريب المنتظم.' },
        ],
      },
      tracks: {
        eyebrow: 'لمن هذه الصفحة',
        title: 'ثلاثة أنواع من المبتدئين',
        subtitle: 'نقطة البداية واحدة، والوتيرة والأسلوب يختلفان.',
        items: [
          { tag: 'الكبار', title: 'لم أتعلم صغيرًا', bullets: ['الحروف والحركات بوتيرة الكبار', 'الشرح بالإنجليزية إن أحببت', 'قراءة صفحة من جزء عمّ أول هدف'] },
          { tag: 'الأطفال', title: 'طفلي يبدأ الآن', bullets: ['جلسات قصيرة مع معلم معتاد على الأطفال', 'تكرار وتشجيع ولوحة متابعة مرئية', 'إفادة للوالد بعد كل جلسة'] },
          { tag: 'العائدون', title: 'تعلمت يومًا ثم نسيت', bullets: ['تقييم سريع لما بقي عندك', 'تصحيح أخطاء تعلّمتها في الصغر', 'الانتقال إلى القراءة بدل البدء من جديد'] },
        ],
      },
      teachers: { eyebrow: 'المعلمون', title: 'معلمون يقبلون المبتدئين', subtitle: 'صفِّ القائمة الكاملة حسب اللغة إن أردت الشرح بالإنجليزية.', viewAll: COMMON_AR.viewAll, empty: COMMON_AR.empty, error: COMMON_AR.error },
      courses: { eyebrow: 'الدورات', title: 'دورات للمبتدئين', viewAll: COMMON_AR.coursesViewAll },
      how: { eyebrow: COMMON_AR.howEyebrow, title: COMMON_AR.howTitle, items: COMMON_AR.how },
      faq: {
        eyebrow: COMMON_AR.faqEyebrow,
        title: COMMON_AR.faqTitle,
        items: [
          { q: 'هل أبدأ ولو كنت لا أقرأ العربية إطلاقًا؟', a: 'نعم، وهؤلاء هم المقصودون بهذه الدروس. تبدأ بالحروف الثمانية والعشرين وأصواتها، ثم الحركات، ثم وصل الحروف في كلمات.' },
          { q: 'متى أستطيع قراءة القرآن؟', a: 'بعشرين دقيقة يوميًا وجلسة أسبوعية، يتعرّف أكثر الكبار على الحروف كلها خلال أسبوعين، ويقرؤون الكلمات المشكولة البسيطة خلال أربعة إلى ستة أسابيع. وقراءة قصار السور من المصحف تحتاج شهرين إلى ثلاثة عادة.' },
          { q: 'هل تأخّرت في السن؟', a: 'لا. الكبار أبطأ من الصغار في الحروف وأسرع في فهم البناء، وأثبت في المراجعة. وكثير من طلاب المنصة بدؤوا في الأربعينيات والخمسينيات.' },
          { q: 'هل تدرّسون بالقاعدة النورانية؟', a: 'أكثر المعلمين يستعملونها أو يسيرون على ترتيبها، لأنها تعرض الحروف والأشكال والحركات والوصل بالترتيب الذي يبني أسرع. واسأل معلمك في الجلسة الأولى.' },
          { q: 'أخجل أن أقرأ أمام أحد، فهل أتعلم؟', a: 'هذا أكثر ما يقوله المبتدئون، والمعلمون يسمعونه كل أسبوع. والجلسات خاصة فردية، والمعلم يستمع ليعرف ما يُصلح لا ليحكم عليك.' },
          { q: 'كم جلسة في الأسبوع؟', a: 'جلسة أو جلستان أسبوعيًا مع تدريب يومي قصير بينهما تكفي أكثر الناس. والتدريب بين الجلسات أهم من عدد الجلسات.' },
        ],
      },
      cta: { title: 'الحرف الأول هو أصعب ما في الأمر', subtitle: 'احجز جلسة واحدة، ودع المعلم يخطط لك ما بعدها.', primary: 'احجز جلستك الأولى', secondary: COMMON_AR.ctaSecondary },
    },
  },

  'noorani-qaida': {
    path: '/noorani-qaida',
    teacherFilter: {},
    courseFilter: { level: 'Beginner' },
    primaryHref: '/register/student',
    en: {
      seo: {
        title: 'Noorani Qaida Classes Online for Kids and Adults | Hudan',
        description: 'Learn the Noorani Qaida online with a live teacher: the letters, their shapes, harakat, sukoon, shaddah and madd, in the order that gets you reading the mushaf fastest.',
        keywords: 'noorani qaida online, noorani qaida classes, qaida for kids, learn noorani qaida, القاعدة النورانية, تعليم القاعدة النورانية أونلاين',
      },
      breadcrumb: 'Noorani Qaida',
      hero: {
        badge: 'The classical beginner primer',
        title: 'Noorani Qaida, taught the way it is meant to be taught',
        subtitle: 'The Qaida is not a book you read; it is a book you recite to a teacher, page by page, until every letter is correct. Live sessions for children and adults, from the first letter to your first page of the mushaf.',
        ctaPrimary: 'Start the Qaida',
        ctaSecondary: 'See teachers for beginners',
        trust: ['For children and adults', 'Correct pronunciation from lesson one', 'English or Arabic explanation', 'Weekly progress you can see'],
      },
      stats: {
        ...COMMON_EN,
        a: { value: '3-6', label: 'Months for most learners' },
        b: { value: '2-3', label: 'Sessions a week suggested' },
      },
      why: {
        eyebrow: 'Why the Qaida',
        title: 'Nine centuries of beginners cannot all be wrong',
        items: [
          { title: 'The right order', desc: 'Letters, then their shapes, then harakat, tanween, sukoon, shaddah and madd — each step depending only on the one before it.' },
          { title: 'Pronunciation from day one', desc: 'The Qaida drills the letter sounds themselves, which is why students who finish it rarely carry the confusions that plague self-taught readers.' },
          { title: 'Made for reciting aloud', desc: 'Every page is designed to be read to a teacher who corrects. Reading it silently to yourself misses the point of the book.' },
          { title: 'Works for any age', desc: 'The same sequence serves a five-year-old and a fifty-year-old; the pace, the examples and the session length change.' },
          { title: 'A clear finish line', desc: 'You finish the Qaida when you can read an unfamiliar page correctly and slowly — a real, testable milestone.' },
          { title: 'Straight into the mushaf', desc: 'Students move from the last pages of the Qaida into Juz Amma, and Tajweed rules begin from there.' },
        ],
      },
      tracks: {
        eyebrow: 'How it is taught',
        title: 'Three groups, one sequence',
        subtitle: 'The book is the same; the session is not.',
        items: [
          { tag: 'Ages 4-7', title: 'First contact with the letters', bullets: ['15-20 minute sessions, more of them', 'Listen and repeat after the teacher', 'Praise, repetition and a visible tracker'] },
          { tag: 'Ages 8-12', title: 'Reading and writing together', bullets: ['30 minute sessions with written practice', 'Letter shapes in all four positions', 'Short surahs alongside the Qaida'] },
          { tag: 'Adults', title: 'Fast, structured, no baby steps', bullets: ['Explanation of the logic, not just drills', 'Two to three lessons per session where possible', 'Straight into Juz Amma when the Qaida ends'] },
        ],
      },
      teachers: { eyebrow: 'Teachers', title: 'Teachers who teach the Qaida', subtitle: 'Ask in your trial session which edition your teacher uses and how they run the pages.', viewAll: COMMON_EN.viewAll, empty: COMMON_EN.empty, error: COMMON_EN.error },
      courses: { eyebrow: 'Courses', title: 'Beginner and Qaida courses', viewAll: COMMON_EN.coursesViewAll },
      how: { eyebrow: COMMON_EN.howEyebrow, title: COMMON_EN.howTitle, items: COMMON_EN.how },
      faq: {
        eyebrow: COMMON_EN.faqEyebrow,
        title: COMMON_EN.faqTitle,
        items: [
          { q: 'What is the Noorani Qaida?', a: 'It is the classical primer used across the Muslim world to teach Quranic reading: the Arabic letters, their shapes in every position, the vowel marks, sukoon, shaddah, tanween and the long vowels, in a sequence designed to be recited to a teacher.' },
          { q: 'How long does it take to finish?', a: 'For most learners, three to six months with two or three sessions a week and short daily practice. Young children usually take longer; adults who practise daily sometimes finish sooner.' },
          { q: 'Is the Qaida only for children?', a: 'No. Adults who cannot yet read Arabic follow the same sequence, usually faster and with more explanation of why each step exists.' },
          { q: 'What comes after the Qaida?', a: 'Reading Juz Amma from the mushaf, and the beginner rules of Tajweed — noon and meem saakinah, the types of madd, and where to stop.' },
          { q: 'Which edition of the book do you use?', a: 'Editions differ slightly in layout but not in sequence. Teachers use the copy they were taught from and will tell you which one in your first session so you can follow along.' },
          { q: 'Can it really be taught online?', a: 'Yes. The Qaida is taught by reciting aloud and being corrected, which video handles well. Audio quality matters, so use a headset with a microphone.' },
        ],
      },
      cta: { title: 'Open the Qaida with a teacher listening', subtitle: 'Book a first session and start at page one this week.', primary: 'Start the Qaida', secondary: COMMON_EN.ctaSecondary },
    },
    ar: {
      seo: {
        title: 'دروس القاعدة النورانية أونلاين للأطفال والكبار | هُدًى للمتقين',
        description: 'تعلّم القاعدة النورانية مع معلم مباشر: الحروف وأشكالها والحركات والسكون والشدة والمد، بالترتيب الذي يُوصلك إلى قراءة المصحف أسرع.',
        keywords: 'القاعدة النورانية, تعليم القاعدة النورانية أونلاين, قاعدة نورانية للأطفال, تعلم القراءة الصحيحة, noorani qaida online',
      },
      breadcrumb: 'القاعدة النورانية',
      hero: {
        badge: 'المتن الأول للمبتدئين',
        title: 'القاعدة النورانية كما ينبغي أن تُدرَّس',
        subtitle: 'القاعدة ليست كتابًا يُقرأ، بل كتابٌ يُتلى على معلم صفحةً صفحة حتى يستقيم كل حرف. جلسات مباشرة للأطفال والكبار، من أول حرف إلى أول صفحة من المصحف.',
        ctaPrimary: 'ابدأ القاعدة',
        ctaSecondary: 'شاهد معلمي المبتدئين',
        trust: ['للأطفال والكبار', 'نطق صحيح من الدرس الأول', 'الشرح بالعربية أو الإنجليزية', 'تقدّم أسبوعي تراه'],
      },
      stats: {
        ...COMMON_AR,
        a: { value: '3-6', label: 'أشهر لأكثر المتعلمين' },
        b: { value: '2-3', label: 'جلسات أسبوعية مقترحة' },
      },
      why: {
        eyebrow: 'لماذا القاعدة',
        title: 'ما اجتمع عليه المبتدئون قرونًا',
        items: [
          { title: 'الترتيب الصحيح', desc: 'الحروف، ثم أشكالها، ثم الحركات والتنوين والسكون والشدة والمد؛ كل خطوة قائمة على التي قبلها وحدها.' },
          { title: 'النطق من أول يوم', desc: 'القاعدة تُدرّب أصوات الحروف نفسها، ولهذا قلّما يحمل من أتمّها الالتباسات التي تلازم من تعلّم وحده.' },
          { title: 'وُضعت لتُقرأ جهرًا', desc: 'كل صفحة مصمّمة لتُتلى على معلم يصحح، ومن قرأها في نفسه فقد فاته مقصود الكتاب.' },
          { title: 'تصلح لكل سن', desc: 'الترتيب نفسه يخدم ابن الخامسة وابن الخمسين، وإنما تختلف الوتيرة والأمثلة وطول الجلسة.' },
          { title: 'نهاية واضحة', desc: 'تُتمّ القاعدة حين تقرأ صفحة لم ترها قراءة صحيحة متمهلة؛ وهو هدف حقيقي قابل للاختبار.' },
          { title: 'ثم إلى المصحف', desc: 'ينتقل الطالب من أواخر القاعدة إلى جزء عمّ، ومن هناك تبدأ أحكام التجويد.' },
        ],
      },
      tracks: {
        eyebrow: 'كيف تُدرَّس',
        title: 'ثلاث فئات وترتيب واحد',
        subtitle: 'الكتاب واحد، والجلسة ليست واحدة.',
        items: [
          { tag: '4-7 سنوات', title: 'أول لقاء بالحروف', bullets: ['جلسات من 15 إلى 20 دقيقة وأكثر عددًا', 'الاستماع والترديد خلف المعلم', 'تشجيع وتكرار ولوحة متابعة مرئية'] },
          { tag: '8-12 سنة', title: 'قراءة وكتابة معًا', bullets: ['جلسات ثلاثين دقيقة مع تدريب كتابي', 'أشكال الحرف في المواضع الأربعة', 'قصار السور مع القاعدة'] },
          { tag: 'الكبار', title: 'سريعة منظمة بلا تبطئة', bullets: ['شرح المنطق لا التلقين وحده', 'درسان أو ثلاثة في الجلسة حين يمكن', 'الانتقال إلى جزء عمّ فور انتهاء القاعدة'] },
        ],
      },
      teachers: { eyebrow: 'المعلمون', title: 'معلمون يُقرئون القاعدة', subtitle: 'اسأل في جلستك التجريبية أي طبعة يعتمدها معلمك وكيف يُدير الصفحات.', viewAll: COMMON_AR.viewAll, empty: COMMON_AR.empty, error: COMMON_AR.error },
      courses: { eyebrow: 'الدورات', title: 'دورات المبتدئين والقاعدة', viewAll: COMMON_AR.coursesViewAll },
      how: { eyebrow: COMMON_AR.howEyebrow, title: COMMON_AR.howTitle, items: COMMON_AR.how },
      faq: {
        eyebrow: COMMON_AR.faqEyebrow,
        title: COMMON_AR.faqTitle,
        items: [
          { q: 'ما القاعدة النورانية؟', a: 'هي المتن المشهور في العالم الإسلامي لتعليم قراءة القرآن: الحروف العربية وأشكالها في كل موضع، والحركات والسكون والشدة والتنوين وحروف المد، بترتيب وُضع ليُتلى على معلم.' },
          { q: 'كم تستغرق حتى تنتهي؟', a: 'لأكثر المتعلمين من ثلاثة إلى ستة أشهر بجلستين أو ثلاث أسبوعيًا مع تدريب يومي قصير. والصغار أطول غالبًا، ومن يتدرب يوميًا من الكبار قد يُتمّها أسرع.' },
          { q: 'هل القاعدة للأطفال فقط؟', a: 'لا. الكبار الذين لا يقرؤون العربية يسيرون على الترتيب نفسه، أسرع غالبًا ومع شرح أوفى لعلة كل خطوة.' },
          { q: 'وماذا بعد القاعدة؟', a: 'قراءة جزء عمّ من المصحف، وأحكام التجويد للمبتدئين: النون والميم الساكنة، وأنواع المدود، ومواضع الوقف.' },
          { q: 'أي طبعة من الكتاب تعتمدون؟', a: 'الطبعات تختلف يسيرًا في الإخراج لا في الترتيب. ويعتمد كل معلم النسخة التي تعلّم منها، ويخبرك بها في الجلسة الأولى لتتابع معه.' },
          { q: 'هل تُدرَّس القاعدة أونلاين حقًا؟', a: 'نعم. فالقاعدة تُدرَّس بالقراءة جهرًا والتصحيح، والفيديو يفي بذلك. والمهم جودة الصوت، فاستعمل سماعة بميكروفون.' },
        ],
      },
      cta: { title: 'افتح القاعدة ومعلمك يسمعك', subtitle: 'احجز جلسة أولى وابدأ من الصفحة الأولى هذا الأسبوع.', primary: 'ابدأ القاعدة', secondary: COMMON_AR.ctaSecondary },
    },
  },

  'one-to-one-quran-classes': {
    path: '/one-to-one-quran-classes',
    teacherFilter: {},
    courseFilter: {},
    primaryHref: '/register/student',
    en: {
      seo: {
        title: 'One to One Quran Classes, Private and Live | Hudan',
        description: 'Private one-to-one Quran lessons on video: the whole session is yours, the teacher corrects every letter, and the plan is built around your level and your week.',
        keywords: 'one to one quran classes, private quran lessons, individual quran tutor, 1 on 1 quran teacher, دروس قرآن فردية, حصص قرآن خاصة',
      },
      breadcrumb: 'One to one classes',
      hero: {
        badge: 'Private, live, on video',
        title: 'The whole session, and the whole teacher, is yours',
        subtitle: 'In a private lesson nobody waits their turn. You recite for most of the session, every mistake is caught, and the plan changes as you do rather than following a fixed syllabus.',
        ctaPrimary: 'Book a private session',
        ctaSecondary: 'Compare teachers',
        trust: ['One student, one teacher', 'Your level, your pace', 'Choose your own schedule', 'Change teacher if the fit is wrong'],
      },
      stats: {
        ...COMMON_EN,
        a: { value: '100%', label: 'Of the session is yours' },
        b: { value: '30-60 min', label: 'Typical session length' },
      },
      why: {
        eyebrow: 'Why private lessons',
        title: 'What changes when nobody else is in the room',
        items: [
          { title: 'You recite more', desc: 'In a group of eight, each student recites for a few minutes. In a private session, reciting is the session.' },
          { title: 'Errors are caught early', desc: 'A mistake corrected in week one takes a minute. The same mistake found six months later has to be unlearned from inside your memorisation.' },
          { title: 'The plan is yours', desc: 'A private teacher can spend three weeks on one letter you cannot produce, or skip what you already have. A syllabus cannot.' },
          { title: 'Your schedule, not a class time', desc: 'Move a session when work or travel demands it instead of missing a fixed weekly slot.' },
          { title: 'Privacy while you learn', desc: 'Adults who are embarrassed to recite in front of others make faster progress when nobody else is listening.' },
          { title: 'A teacher who knows you', desc: 'After a month, your teacher knows your weak letters, your revision habits and how much you can really take each week.' },
        ],
      },
      tracks: {
        eyebrow: 'What a session looks like',
        title: 'Three formats people book',
        subtitle: 'Agree the shape with your teacher in the trial session.',
        items: [
          { tag: 'Correction', title: 'Recite and be corrected', bullets: ['You read, the teacher stops you at each fault', 'Targeted drills for the letters you confuse', 'Homework to practise between sessions'] },
          { tag: 'Memorisation', title: 'New portion and revision', bullets: ['Recite yesterday, then take today', 'Weekly recitation of older portions', 'Records kept per surah in your dashboard'] },
          { tag: 'Study', title: 'Rules and meaning', bullets: ['Tajweed rules explained and applied', 'Word-by-word meaning of what you recite', 'Questions answered as they come up'] },
        ],
      },
      teachers: { eyebrow: 'Teachers', title: 'Book a private teacher', subtitle: 'Rates, languages and specialties are on each profile. Many teachers offer a trial session.', viewAll: COMMON_EN.viewAll, empty: COMMON_EN.empty, error: COMMON_EN.error },
      courses: { eyebrow: 'Courses', title: 'Courses you can take one to one', viewAll: COMMON_EN.coursesViewAll },
      how: { eyebrow: COMMON_EN.howEyebrow, title: COMMON_EN.howTitle, items: COMMON_EN.how },
      faq: {
        eyebrow: COMMON_EN.faqEyebrow,
        title: COMMON_EN.faqTitle,
        items: [
          { q: 'Is one to one better than a group class?', a: 'For correcting recitation, yes: you recite for most of the session and every error is heard. Group classes are cheaper and suit students who mainly need structure and company rather than correction.' },
          { q: 'How long should a private session be?', a: 'Thirty minutes suits children and daily hifz; sixty minutes suits adults working on Tajweed or covering several things in one sitting. Frequency matters more than length.' },
          { q: 'Can I change teacher later?', a: 'Yes. You are not tied to one teacher — book a trial with someone else whenever you want, and your progress records stay in your dashboard.' },
          { q: 'What if I need to reschedule?', a: 'Sessions can be rescheduled with your teacher through the platform. Each teacher sets their own notice period, which is shown when you book.' },
          { q: 'Do you offer private classes for children?', a: 'Yes. Parents can create a family account, add each child, and book a teacher who specialises in teaching children.' },
          { q: 'Is a private class more expensive?', a: 'Per hour, usually yes. Per unit of progress it is often cheaper, because the whole hour is spent on you rather than on the group.' },
        ],
      },
      cta: { title: 'One teacher, listening only to you', subtitle: 'Book a private session this week and see the difference in one hour.', primary: 'Book a private session', secondary: COMMON_EN.ctaSecondary },
    },
    ar: {
      seo: {
        title: 'دروس قرآن فردية خاصة ومباشرة | هُدًى للمتقين',
        description: 'دروس قرآن فردية عبر الفيديو: الجلسة كلها لك، والمعلم يصحح كل حرف، والخطة مبنية على مستواك وأسبوعك.',
        keywords: 'دروس قرآن فردية, حصص قرآن خاصة, معلم قرآن خاص, تعليم فردي للقرآن, one to one quran classes',
      },
      breadcrumb: 'الدروس الفردية',
      hero: {
        badge: 'خاصة، مباشرة، عبر الفيديو',
        title: 'الجلسة كلها لك، والمعلم كله لك',
        subtitle: 'في الدرس الخاص لا ينتظر أحد دوره. تتلو معظم الجلسة، ويُلتقط كل خطأ، وتتغير الخطة بتغيّرك لا وفق منهج ثابت.',
        ctaPrimary: 'احجز جلسة خاصة',
        ctaSecondary: 'قارن بين المعلمين',
        trust: ['طالب واحد ومعلم واحد', 'مستواك ووتيرتك', 'أنت تختار المواعيد', 'غيّر المعلم إن لم يناسبك'],
      },
      stats: {
        ...COMMON_AR,
        a: { value: '100%', label: 'من الجلسة لك' },
        b: { value: '30-60 دقيقة', label: 'مدة الجلسة المعتادة' },
      },
      why: {
        eyebrow: 'لماذا الدرس الخاص',
        title: 'ما الذي يتغير حين لا يكون في الغرفة أحد سواك',
        items: [
          { title: 'تتلو أكثر', desc: 'في حلقة من ثمانية يتلو كل طالب دقائق. وفي الجلسة الخاصة التلاوة هي الجلسة.' },
          { title: 'الخطأ يُلتقط مبكرًا', desc: 'خطأ يُصحَّح في الأسبوع الأول يكلف دقيقة، والخطأ نفسه بعد ستة أشهر يُنقض من داخل حفظك.' },
          { title: 'الخطة خطتك', desc: 'المعلم الخاص يستطيع أن يقضي ثلاثة أسابيع على حرف لا تُحسنه، أو يتجاوز ما أتقنته. والمنهج الجاهز لا يستطيع.' },
          { title: 'مواعيدك لا موعد الحلقة', desc: 'انقل الجلسة إذا اقتضى عمل أو سفر بدل أن تفوتك حصة أسبوعية ثابتة.' },
          { title: 'خصوصية أثناء التعلم', desc: 'الكبار الذين يخجلون من القراءة أمام الناس يتقدمون أسرع حين لا يسمعهم أحد.' },
          { title: 'معلم يعرفك', desc: 'بعد شهر يعرف معلمك حروفك الضعيفة وعاداتك في المراجعة وما تحتمله فعلًا كل أسبوع.' },
        ],
      },
      tracks: {
        eyebrow: 'كيف تبدو الجلسة',
        title: 'ثلاث صيغ يحجزها الناس',
        subtitle: 'اتفق مع معلمك على الصيغة في الجلسة التجريبية.',
        items: [
          { tag: 'تصحيح', title: 'اقرأ ويُصحَّح لك', bullets: ['تقرأ والمعلم يوقفك عند كل خطأ', 'تدريبات موجّهة للحروف التي تلتبس عليك', 'واجب للتدريب بين الجلسات'] },
          { tag: 'حفظ', title: 'جديد ومراجعة', bullets: ['تسرد الأمس ثم تأخذ اليوم', 'سرد أسبوعي للمحفوظ القديم', 'سجل لكل سورة في لوحتك'] },
          { tag: 'دراسة', title: 'أحكام ومعانٍ', bullets: ['شرح أحكام التجويد وتطبيقها', 'معاني ما تتلوه كلمة كلمة', 'إجابة أسئلتك حين تَرِد'] },
        ],
      },
      teachers: { eyebrow: 'المعلمون', title: 'احجز معلمًا خاصًا', subtitle: 'الأسعار واللغات والتخصصات في ملف كل معلم، وكثيرون يقدمون جلسة تجريبية.', viewAll: COMMON_AR.viewAll, empty: COMMON_AR.empty, error: COMMON_AR.error },
      courses: { eyebrow: 'الدورات', title: 'دورات تدرسها فرديًا', viewAll: COMMON_AR.coursesViewAll },
      how: { eyebrow: COMMON_AR.howEyebrow, title: COMMON_AR.howTitle, items: COMMON_AR.how },
      faq: {
        eyebrow: COMMON_AR.faqEyebrow,
        title: COMMON_AR.faqTitle,
        items: [
          { q: 'هل الدرس الفردي خير من الحلقة؟', a: 'لتصحيح التلاوة نعم؛ فأنت تتلو معظم الجلسة ويُسمع كل خطأ. والحلقات أقل كلفة وتناسب من يحتاج التنظيم والصحبة أكثر من التصحيح.' },
          { q: 'كم ينبغي أن تطول الجلسة الخاصة؟', a: 'ثلاثون دقيقة تناسب الأطفال والحفظ اليومي، وستون تناسب الكبار في التجويد أو من يجمع أمورًا في مجلس واحد. والتكرار أهم من الطول.' },
          { q: 'هل أستطيع تغيير المعلم لاحقًا؟', a: 'نعم. لست مرتبطًا بمعلم واحد؛ احجز جلسة تجريبية مع غيره متى شئت، وتبقى سجلات تقدمك في لوحتك.' },
          { q: 'وإذا احتجت تأجيل موعد؟', a: 'يمكن إعادة جدولة الجلسات مع معلمك عبر المنصة، ولكل معلم مدة إشعار يحددها وتظهر لك عند الحجز.' },
          { q: 'هل توجد دروس خاصة للأطفال؟', a: 'نعم. يستطيع الوالد إنشاء حساب عائلي وإضافة كل طفل وحجز معلم متخصص في تعليم الأطفال.' },
          { q: 'هل الدرس الخاص أغلى؟', a: 'في الساعة نعم غالبًا. وأما في مقدار التقدم فهو أرخص كثيرًا، لأن الساعة كلها صُرفت عليك لا على المجموعة.' },
        ],
      },
      cta: { title: 'معلم واحد لا يستمع إلا إليك', subtitle: 'احجز جلسة خاصة هذا الأسبوع، وسترى الفرق في ساعة.', primary: 'احجز جلسة خاصة', secondary: COMMON_AR.ctaSecondary },
    },
  },

  'quran-classes-usa': {
    path: '/quran-classes-usa',
    teacherFilter: { language: 'English' },
    courseFilter: {},
    primaryHref: '/register/student',
    en: {
      seo: {
        title: 'Online Quran Classes in the USA, English-Speaking Teachers | Hudan',
        description: 'Online Quran classes for Muslim families in the United States: English-speaking verified teachers, evening and weekend slots across US time zones, one to one on video.',
        keywords: 'quran classes usa, online quran classes america, quran teacher for kids usa, quran tutor english speaking, دروس قرآن للجالية في أمريكا',
      },
      breadcrumb: 'Quran classes in the USA',
      hero: {
        badge: 'For families in the United States',
        title: 'Quran classes that fit an American week',
        subtitle: 'After school, after Isha, or Saturday morning — book live one-to-one sessions with verified teachers who explain in English, without driving anyone across town.',
        ctaPrimary: 'Book a trial session',
        ctaSecondary: 'See English-speaking teachers',
        trust: ['Teachers who explain in English', 'Evening and weekend slots', 'Prices shown in US dollars', 'Male and female teachers'],
      },
      stats: {
        ...COMMON_EN,
        a: { value: 'English', label: 'Explanation language' },
        b: { value: 'USD', label: 'Prices shown in' },
      },
      why: {
        eyebrow: 'Why families here choose online',
        title: 'The weekend Islamic school problem, solved',
        items: [
          { title: 'No commute', desc: 'A thirty minute lesson costs thirty minutes, not an hour of driving on either side of it.' },
          { title: 'Explanation in English', desc: 'Children raised in the US follow an English explanation of a rule far faster, even when the recitation itself is in Arabic.' },
          { title: 'Times that work here', desc: 'Teachers keep slots open for US evenings and weekend mornings. Set your time zone once and every time you see is local.' },
          { title: 'One to one, not a class of thirty', desc: 'Weekend school gives each child a few minutes of attention; a private session gives them all of it.' },
          { title: 'Parents can see the progress', desc: 'A family account shows each child, their sessions, homework and teacher notes in one place.' },
          { title: 'Teachers you can vet', desc: 'Every teacher is reviewed before being listed, and Ijazah-verified teachers carry a badge. You read reviews and choose.' },
        ],
      },
      tracks: {
        eyebrow: 'Who books from the US',
        title: 'Three common situations',
        subtitle: 'Say which one is yours in the trial session.',
        items: [
          { tag: 'Children', title: 'My kids need more than weekend school', bullets: ['Reading and Tajweed with an English-speaking teacher', 'Short sessions after school or after Isha', 'Parent updates and homework you can check'] },
          { tag: 'Adults', title: 'I want to fix my own recitation', bullets: ['Evening sessions after work', 'Correction of habits learned as a child', 'A route into memorisation when you are ready'] },
          { tag: 'Converts', title: 'I am new to Islam', bullets: ['Start with the letters, no assumptions', 'Prayer surahs first, explained in English', 'A patient teacher who has taught converts before'] },
        ],
      },
      teachers: { eyebrow: 'Teachers', title: 'Teachers who teach in English', subtitle: 'This page lists teachers who include English among their teaching languages. Set your time zone in settings to see slots in local time.', viewAll: COMMON_EN.viewAll, empty: COMMON_EN.empty, error: COMMON_EN.error },
      courses: { eyebrow: 'Courses', title: 'Courses to start with', viewAll: COMMON_EN.coursesViewAll },
      how: { eyebrow: COMMON_EN.howEyebrow, title: COMMON_EN.howTitle, items: COMMON_EN.how },
      faq: {
        eyebrow: COMMON_EN.faqEyebrow,
        title: COMMON_EN.faqTitle,
        items: [
          { q: 'Are there classes at US-friendly times?', a: 'Yes. Teachers set their own availability and many keep slots for US evenings and weekend mornings. Times display in your own time zone once you set it, so there is no arithmetic to do.' },
          { q: 'Do teachers speak English?', a: 'The teachers listed on this page include English among their teaching languages. The recitation is in Arabic; the explanation can be in English.' },
          { q: 'How much do classes cost in dollars?', a: 'Each teacher sets an hourly rate, shown on their profile, and many offer a discounted trial. Prices display in US dollars and charges are processed in US dollars.' },
          { q: 'Is it suitable for children born in the US?', a: 'Yes, and it is the most common case. Teachers who work with American children explain in English, keep sessions short, and set homework that a parent can check.' },
          { q: 'Can I request a female teacher for my daughter?', a: 'Yes. Filter the teacher list by gender or start from the female teachers page.' },
          { q: 'What do we need to get started?', a: 'A laptop or tablet, a stable connection and a headset with a microphone. Create a free account, pick a teacher, and book a time.' },
        ],
      },
      cta: { title: 'Book the first session for this week', subtitle: 'Create a free family account and choose a teacher tonight.', primary: 'Book a trial session', secondary: COMMON_EN.ctaSecondary },
    },
    ar: {
      seo: {
        title: 'دروس قرآن أونلاين للمقيمين في أمريكا | هُدًى للمتقين',
        description: 'دروس قرآن مباشرة للأسر المسلمة في الولايات المتحدة: معلمون موثّقون يشرحون بالإنجليزية، ومواعيد مسائية ونهاية الأسبوع بتوقيتك، فردًا لفرد عبر الفيديو.',
        keywords: 'دروس قرآن في أمريكا, تحفيظ قرآن للجالية, معلم قرآن يتحدث الإنجليزية, quran classes usa',
      },
      breadcrumb: 'دروس القرآن في أمريكا',
      hero: {
        badge: 'للأسر في الولايات المتحدة',
        title: 'دروس قرآن تناسب الأسبوع الأمريكي',
        subtitle: 'بعد المدرسة، أو بعد العشاء، أو صباح السبت — احجز جلسات مباشرة فردية مع معلمين موثّقين يشرحون بالإنجليزية، بلا قيادة عبر المدينة.',
        ctaPrimary: 'احجز جلسة تجريبية',
        ctaSecondary: 'شاهد المعلمين بالإنجليزية',
        trust: ['معلمون يشرحون بالإنجليزية', 'مواعيد مسائية ونهاية الأسبوع', 'الأسعار بالدولار الأمريكي', 'معلمون ومعلمات'],
      },
      stats: {
        ...COMMON_AR,
        a: { value: 'الإنجليزية', label: 'لغة الشرح' },
        b: { value: 'دولار', label: 'عملة عرض الأسعار' },
      },
      why: {
        eyebrow: 'لماذا تختار الأسر هنا الإنترنت',
        title: 'حلٌّ لمشكلة مدرسة نهاية الأسبوع',
        items: [
          { title: 'بلا تنقل', desc: 'درس ثلاثين دقيقة يكلفك ثلاثين دقيقة، لا ساعة قيادة قبله ومثلها بعده.' },
          { title: 'شرح بالإنجليزية', desc: 'الأطفال الذين نشؤوا في أمريكا يستوعبون شرح الحكم بالإنجليزية أسرع، وإن كانت التلاوة نفسها بالعربية.' },
          { title: 'مواعيد تصلح هنا', desc: 'يفتح المعلمون مواعيد لأمسيات أمريكا وصباح العطلة. اضبط منطقتك الزمنية مرة، فتظهر كل المواعيد بتوقيتك.' },
          { title: 'فردي لا فصل من ثلاثين', desc: 'مدرسة العطلة تمنح كل طفل دقائق، والجلسة الخاصة تمنحه الوقت كله.' },
          { title: 'الوالدان يريان التقدم', desc: 'الحساب العائلي يعرض كل طفل وجلساته وواجباته وملاحظات معلمه في مكان واحد.' },
          { title: 'معلمون تتحقق منهم', desc: 'كل معلم يُراجَع قبل إدراجه، ومن وُثّقت إجازته يحمل شارة. تقرأ التقييمات وتختار.' },
        ],
      },
      tracks: {
        eyebrow: 'من يحجز من أمريكا',
        title: 'ثلاث حالات شائعة',
        subtitle: 'اذكر حالتك في الجلسة التجريبية.',
        items: [
          { tag: 'الأطفال', title: 'أولادي يحتاجون أكثر من مدرسة العطلة', bullets: ['قراءة وتجويد مع معلم يشرح بالإنجليزية', 'جلسات قصيرة بعد المدرسة أو بعد العشاء', 'إفادات للوالدين وواجبات يمكن متابعتها'] },
          { tag: 'الكبار', title: 'أريد إصلاح تلاوتي', bullets: ['جلسات مسائية بعد العمل', 'تصحيح عادات تعلّمتها صغيرًا', 'مسار إلى الحفظ حين تستعد'] },
          { tag: 'المسلمون الجدد', title: 'أنا حديث عهد بالإسلام', bullets: ['البداية من الحروف بلا افتراضات', 'سور الصلاة أولًا مشروحة بالإنجليزية', 'معلم صبور سبق أن علّم مسلمين جددًا'] },
        ],
      },
      teachers: { eyebrow: 'المعلمون', title: 'معلمون يدرّسون بالإنجليزية', subtitle: 'تعرض هذه الصفحة من يذكرون الإنجليزية ضمن لغات التدريس. اضبط منطقتك الزمنية في الإعدادات لترى المواعيد بتوقيتك.', viewAll: COMMON_AR.viewAll, empty: COMMON_AR.empty, error: COMMON_AR.error },
      courses: { eyebrow: 'الدورات', title: 'دورات تبدأ بها', viewAll: COMMON_AR.coursesViewAll },
      how: { eyebrow: COMMON_AR.howEyebrow, title: COMMON_AR.howTitle, items: COMMON_AR.how },
      faq: {
        eyebrow: COMMON_AR.faqEyebrow,
        title: COMMON_AR.faqTitle,
        items: [
          { q: 'هل توجد مواعيد تناسب توقيت أمريكا؟', a: 'نعم. يحدد كل معلم أوقاته، وكثيرون يفتحون مواعيد لأمسيات أمريكا وصباح العطلة. وتظهر المواعيد بتوقيتك بعد ضبط منطقتك الزمنية، فلا حساب عليك.' },
          { q: 'هل يتحدث المعلمون الإنجليزية؟', a: 'المعلمون في هذه الصفحة يذكرون الإنجليزية ضمن لغات التدريس. والتلاوة بالعربية، والشرح يمكن أن يكون بالإنجليزية.' },
          { q: 'كم تكلفة الدروس بالدولار؟', a: 'كل معلم يحدد أجره بالساعة وهو ظاهر في ملفه، وكثيرون يقدمون جلسة تجريبية مخفَّضة. وتُعرض الأسعار بالدولار الأمريكي والتحصيل بالدولار.' },
          { q: 'هل تناسب الأطفال المولودين في أمريكا؟', a: 'نعم، وهي الحالة الأكثر شيوعًا. والمعلمون الذين يعملون مع الأطفال هناك يشرحون بالإنجليزية، ويجعلون الجلسات قصيرة، ويكلفون بواجب يستطيع الوالد متابعته.' },
          { q: 'هل أطلب معلمة لابنتي؟', a: 'نعم. صفِّ قائمة المعلمين حسب الجنس أو ابدأ من صفحة المعلمات.' },
          { q: 'ما الذي نحتاجه للبدء؟', a: 'حاسوب أو جهاز لوحي، واتصال مستقر، وسماعة بميكروفون. أنشئ حسابًا مجانيًا، واختر معلمًا، واحجز موعدًا.' },
        ],
      },
      cta: { title: 'اجعل الجلسة الأولى هذا الأسبوع', subtitle: 'أنشئ حسابًا عائليًا مجانيًا واختر معلمك الليلة.', primary: 'احجز جلسة تجريبية', secondary: COMMON_AR.ctaSecondary },
    },
  },

  'quran-classes-uk': {
    path: '/quran-classes-uk',
    teacherFilter: { language: 'English' },
    courseFilter: {},
    primaryHref: '/register/student',
    en: {
      seo: {
        title: 'Online Quran Classes in the UK, English-Speaking Teachers | Hudan',
        description: 'Online Quran classes for families in the United Kingdom: verified teachers who explain in English, after-school and evening slots in UK time, one to one on video.',
        keywords: 'quran classes uk, online quran classes london, quran teacher uk, quran tutor for kids uk, دروس قرآن في بريطانيا',
      },
      breadcrumb: 'Quran classes in the UK',
      hero: {
        badge: 'For families in the United Kingdom',
        title: 'Quran classes after school, without the madrasah run',
        subtitle: 'Live one-to-one sessions with verified teachers who explain in English, at UK-friendly times, for children who go to school here and adults who work here.',
        ctaPrimary: 'Book a trial session',
        ctaSecondary: 'See English-speaking teachers',
        trust: ['Teachers who explain in English', 'After-school and evening slots', 'Prices can display in pounds', 'Male and female teachers'],
      },
      stats: {
        ...COMMON_EN,
        a: { value: 'English', label: 'Explanation language' },
        b: { value: 'GBP', label: 'Display currency available' },
      },
      why: {
        eyebrow: 'Why families here choose online',
        title: 'Better than the 5pm dash across town',
        items: [
          { title: 'No travel, no waiting', desc: 'The session starts when it starts. Nobody sits in a car park or a corridor waiting for a turn.' },
          { title: 'One to one attention', desc: 'A local class of twenty gives each child a couple of minutes of recitation. A private session is thirty minutes of it.' },
          { title: 'English explanation, Arabic recitation', desc: 'Children schooled in the UK follow the rules faster in English while learning to recite in Arabic properly.' },
          { title: 'UK-friendly times', desc: 'Teachers keep after-school and evening slots. Set your time zone once and everything you see is in UK time.' },
          { title: 'You see what happened', desc: 'Session notes, homework and progress per child in a family account, rather than asking a child what they covered.' },
          { title: 'Choose the teacher yourself', desc: 'Reviews, ratings, rates, languages and Ijazah badges are all on the profile before you book.' },
        ],
      },
      tracks: {
        eyebrow: 'Who books from the UK',
        title: 'Three common situations',
        subtitle: 'Say which one is yours in the trial session.',
        items: [
          { tag: 'Children', title: 'My child needs steady progress', bullets: ['Reading, Tajweed and Hifz at their own pace', 'Short after-school or early evening sessions', 'Homework and notes a parent can follow'] },
          { tag: 'Adults', title: 'I never finished learning', bullets: ['Evening sessions after work', 'Correction of childhood mistakes without embarrassment', 'A plan you can keep in a busy week'] },
          { tag: 'Reverts', title: 'I recently became Muslim', bullets: ['Letters and prayer surahs first', 'Everything explained in English', 'A patient teacher, no assumed background'] },
        ],
      },
      teachers: { eyebrow: 'Teachers', title: 'Teachers who teach in English', subtitle: 'This page lists teachers who include English among their teaching languages. Set your time zone in settings to see slots in UK time.', viewAll: COMMON_EN.viewAll, empty: COMMON_EN.empty, error: COMMON_EN.error },
      courses: { eyebrow: 'Courses', title: 'Courses to start with', viewAll: COMMON_EN.coursesViewAll },
      how: { eyebrow: COMMON_EN.howEyebrow, title: COMMON_EN.howTitle, items: COMMON_EN.how },
      faq: {
        eyebrow: COMMON_EN.faqEyebrow,
        title: COMMON_EN.faqTitle,
        items: [
          { q: 'Are there classes at UK-friendly times?', a: 'Yes. Teachers set their own availability and many keep after-school and evening slots. Once you set your time zone, every slot you see is displayed in UK time.' },
          { q: 'Is this a replacement for the local madrasah?', a: 'It can be, and many families use both. Online sessions give one-to-one correction that a class of twenty cannot; the local madrasah gives community and routine.' },
          { q: 'How much do classes cost in pounds?', a: 'Each teacher sets an hourly rate shown on their profile, and many offer a discounted trial. Prices can be displayed in pounds; charges are processed in US dollars.' },
          { q: 'Can my daughter learn with a female teacher?', a: 'Yes. Filter the teacher list by gender, or start from the female teachers page.' },
          { q: 'What age can children start?', a: 'Most teachers take children from around five, when a child can sit for fifteen to twenty minutes and repeat after a teacher. Younger children can start with very short sessions.' },
          { q: 'What equipment do we need?', a: 'A laptop or tablet, a stable connection, and a headset with a microphone. Audio quality matters far more than video quality.' },
        ],
      },
      cta: { title: 'Start this week, from home', subtitle: 'Create a free family account and book a trial session tonight.', primary: 'Book a trial session', secondary: COMMON_EN.ctaSecondary },
    },
    ar: {
      seo: {
        title: 'دروس قرآن أونلاين للمقيمين في بريطانيا | هُدًى للمتقين',
        description: 'دروس قرآن مباشرة للأسر في المملكة المتحدة: معلمون موثّقون يشرحون بالإنجليزية، ومواعيد بعد المدرسة ومسائية بتوقيت بريطانيا، فردًا لفرد عبر الفيديو.',
        keywords: 'دروس قرآن في بريطانيا, تحفيظ قرآن لندن, معلم قرآن في المملكة المتحدة, quran classes uk',
      },
      breadcrumb: 'دروس القرآن في بريطانيا',
      hero: {
        badge: 'للأسر في المملكة المتحدة',
        title: 'دروس قرآن بعد المدرسة بلا رحلة إلى المدرسة القرآنية',
        subtitle: 'جلسات مباشرة فردية مع معلمين موثّقين يشرحون بالإنجليزية، في مواعيد تناسب بريطانيا، لأطفال يدرسون هناك وكبار يعملون هناك.',
        ctaPrimary: 'احجز جلسة تجريبية',
        ctaSecondary: 'شاهد المعلمين بالإنجليزية',
        trust: ['معلمون يشرحون بالإنجليزية', 'مواعيد بعد المدرسة ومسائية', 'يمكن عرض الأسعار بالجنيه', 'معلمون ومعلمات'],
      },
      stats: {
        ...COMMON_AR,
        a: { value: 'الإنجليزية', label: 'لغة الشرح' },
        b: { value: 'جنيه', label: 'عملة عرض متاحة' },
      },
      why: {
        eyebrow: 'لماذا تختار الأسر هناك الإنترنت',
        title: 'خير من الركض في الخامسة عصرًا',
        items: [
          { title: 'بلا تنقل ولا انتظار', desc: 'تبدأ الجلسة في وقتها، ولا أحد ينتظر دوره في موقف أو ممر.' },
          { title: 'اهتمام فردي', desc: 'الحلقة المحلية من عشرين تمنح كل طفل دقيقتين من التلاوة، والجلسة الخاصة ثلاثون دقيقة منها.' },
          { title: 'شرح بالإنجليزية وتلاوة بالعربية', desc: 'الأطفال الذين يدرسون في بريطانيا يستوعبون الأحكام بالإنجليزية أسرع، ويتعلمون التلاوة بالعربية على وجهها.' },
          { title: 'مواعيد تناسب بريطانيا', desc: 'يفتح المعلمون مواعيد بعد المدرسة وفي المساء. اضبط منطقتك الزمنية مرة، فيظهر كل شيء بتوقيت بريطانيا.' },
          { title: 'ترى ما جرى', desc: 'ملاحظات الجلسة والواجبات وتقدّم كل طفل في حساب عائلي، بدل أن تسأل الطفل عمّا درسه.' },
          { title: 'أنت تختار المعلم', desc: 'التقييمات والأسعار واللغات وشارات الإجازة كلها في الملف قبل أن تحجز.' },
        ],
      },
      tracks: {
        eyebrow: 'من يحجز من بريطانيا',
        title: 'ثلاث حالات شائعة',
        subtitle: 'اذكر حالتك في الجلسة التجريبية.',
        items: [
          { tag: 'الأطفال', title: 'ابني يحتاج تقدمًا منتظمًا', bullets: ['قراءة وتجويد وحفظ بوتيرته', 'جلسات قصيرة بعد المدرسة أو أول المساء', 'واجبات وملاحظات يتابعها الوالد'] },
          { tag: 'الكبار', title: 'لم أُكمل تعلمي يومًا', bullets: ['جلسات مسائية بعد العمل', 'تصحيح أخطاء الصغر بلا حرج', 'خطة تصمد في أسبوع مزدحم'] },
          { tag: 'المسلمون الجدد', title: 'أسلمتُ حديثًا', bullets: ['الحروف وسور الصلاة أولًا', 'كل شيء مشروح بالإنجليزية', 'معلم صبور لا يفترض خلفية سابقة'] },
        ],
      },
      teachers: { eyebrow: 'المعلمون', title: 'معلمون يدرّسون بالإنجليزية', subtitle: 'تعرض هذه الصفحة من يذكرون الإنجليزية ضمن لغات التدريس. اضبط منطقتك الزمنية في الإعدادات لترى المواعيد بتوقيت بريطانيا.', viewAll: COMMON_AR.viewAll, empty: COMMON_AR.empty, error: COMMON_AR.error },
      courses: { eyebrow: 'الدورات', title: 'دورات تبدأ بها', viewAll: COMMON_AR.coursesViewAll },
      how: { eyebrow: COMMON_AR.howEyebrow, title: COMMON_AR.howTitle, items: COMMON_AR.how },
      faq: {
        eyebrow: COMMON_AR.faqEyebrow,
        title: COMMON_AR.faqTitle,
        items: [
          { q: 'هل توجد مواعيد تناسب توقيت بريطانيا؟', a: 'نعم. يحدد كل معلم أوقاته، وكثيرون يفتحون مواعيد بعد المدرسة وفي المساء. وبعد ضبط منطقتك الزمنية تظهر كل المواعيد بتوقيت بريطانيا.' },
          { q: 'هل تُغني عن المدرسة القرآنية المحلية؟', a: 'قد تُغني، وكثير من الأسر تجمع بينهما. فالجلسات أونلاين تمنح تصحيحًا فرديًا لا تمنحه حلقة من عشرين، والمدرسة المحلية تمنح الصحبة والانتظام.' },
          { q: 'كم تكلفة الدروس بالجنيه؟', a: 'كل معلم يحدد أجره بالساعة وهو ظاهر في ملفه، وكثيرون يقدمون جلسة تجريبية مخفَّضة. ويمكن عرض الأسعار بالجنيه، والتحصيل بالدولار الأمريكي.' },
          { q: 'هل تتعلم ابنتي مع معلمة؟', a: 'نعم. صفِّ قائمة المعلمين حسب الجنس أو ابدأ من صفحة المعلمات.' },
          { q: 'من أي سن يبدأ الأطفال؟', a: 'أكثر المعلمين يقبلون من نحو الخامسة، حين يستطيع الطفل الجلوس خمس عشرة إلى عشرين دقيقة والترديد خلف معلمه. ومن هم أصغر يبدؤون بجلسات قصيرة جدًا.' },
          { q: 'ما العُدّة التي نحتاجها؟', a: 'حاسوب أو جهاز لوحي، واتصال مستقر، وسماعة بميكروفون. وجودة الصوت أهم بكثير من جودة الصورة.' },
        ],
      },
      cta: { title: 'ابدأ هذا الأسبوع من البيت', subtitle: 'أنشئ حسابًا عائليًا مجانيًا واحجز جلسة تجريبية الليلة.', primary: 'احجز جلسة تجريبية', secondary: COMMON_AR.ctaSecondary },
    },
  },
};

export const LANDING_PATHS = Object.values(LANDINGS).map((l) => l.path);
